/**
 * DetalleProyectoModal
 * Modal fullscreen con pestañas para ver toda la información del proyecto
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  presupuestoVentaService,
  presupuestoCostoService,
  ordenCompraService,
  detalleOrdenCompraService,
  itemPresupuestoVentaService,
  itemPresupuestoCostoService,
  rendicionService,
  detalleRendicionService,
  proveedorService,
  categoriaPresupuestoService,
  proyectoService,
  clienteService,
  lineaNegocioService,
  imputacionCostoService,
} from '../services/onebaseService';
import eventosService from '../services/eventosService';
import eventosGanttService from '../services/eventosGanttService';
import presupuestoHierarchyService from '../services/presupuestoHierarchyService';
import bodegaService from '../services/bodegaService';
import AuditHistory from './AuditHistory';
import AgregarItemModal from './AgregarItemModal';
import CrearOrdenCompraOneBaseModal from './CrearOrdenCompraOneBaseModal';
import VerEditarOrdenCompraModal from './VerEditarOrdenCompraModal';
import SeleccionarItemsPresupuestoModal from './SeleccionarItemsPresupuestoModal';
import AsignarAreasModal from './AsignarAreasModal';
import PresupuestoVentaHierarchy from './PresupuestoVentaHierarchy';
import PresupuestoCostoHierarchy from './PresupuestoCostoHierarchy';
import AsignarDesdeCatalogoModal from './AsignarDesdeCatalogoModal';
import AgregarAreaPresupuestoModal from './AgregarAreaPresupuestoModal';
import AsignarPresupuestoCostoModal from './AsignarPresupuestoCostoModal';
import GanttChart from './GanttChart';
import CrearMovimientoBodegaModal from './CrearMovimientoBodegaModal';
import { FiX, FiDollarSign, FiFileText, FiShoppingCart, FiFolder, FiPlus, FiEye, FiEdit, FiTrash2, FiSend, FiCheck, FiTrendingUp, FiCheckCircle, FiXCircle, FiChevronDown, FiChevronUp, FiCopy, FiLink2, FiPackage } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import './DetalleProyectoModal.css';
import '../pages/MovimientosBodegaPage.css';

import Loading from './Loading';
const DetalleProyectoModal = ({ proyecto, onClose, onUpdate }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [selectedPresupuestoVenta, setSelectedPresupuestoVenta] = useState(null);
  const [selectedPresupuestoCosto, setSelectedPresupuestoCosto] = useState(null);
  const [asignarAreasModalOpen, setAsignarAreasModalOpen] = useState(false);
  const [presupuestoParaAsignarAreas, setPresupuestoParaAsignarAreas] = useState(null);
  const [tipoPresupuestoParaAsignarAreas, setTipoPresupuestoParaAsignarAreas] = useState('venta');
  const [asignarDesdeCatalogoModalOpen, setAsignarDesdeCatalogoModalOpen] = useState(false);
  const [presupuestoParaAsignarCatalogo, setPresupuestoParaAsignarCatalogo] = useState(null);
  const [tipoPresupuestoParaAsignarCatalogo, setTipoPresupuestoParaAsignarCatalogo] = useState('venta');
  const [agregarAreaModalOpen, setAgregarAreaModalOpen] = useState(false);
  const [presupuestoParaAgregarArea, setPresupuestoParaAgregarArea] = useState(null);
  const [tipoPresupuestoParaAgregarArea, setTipoPresupuestoParaAgregarArea] = useState('venta');
  const [asignarPresupuestoCostoModalOpen, setAsignarPresupuestoCostoModalOpen] = useState(false);
  const [presupuestoVentaParaCosto, setPresupuestoVentaParaCosto] = useState(null);

  // Estados para tareas del proyecto
  const [tareasDelProyecto, setTareasDelProyecto] = useState([]);
  const [loadingTareas, setLoadingTareas] = useState(false);
  const [showManualTimeForm, setShowManualTimeForm] = useState(false);
  const [selectedTareaForTime, setSelectedTareaForTime] = useState(null);
  const [manualTimeForm, setManualTimeForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    minutos: '',
    notas: '',
    presupuesto_costo_recursos: []
  });
  const [recursosDisponiblesTarea, setRecursosDisponiblesTarea] = useState([]);

  // Mapeo de estados a colores y labels legibles
  const estadoColors = {
    nuevo: '#9ca3af',
    presupuesto_venta_en_trabajo: '#3b82f6',
    presupuesto_venta_en_aprobacion_interna: '#69B32D',
    presupuesto_venta_aprobado_internamente: '#69B32D',
    presupuesto_venta_enviado_a_cliente: '#06b6d4',
    presupuesto_venta_aprobado_cliente: '#10b981',
    en_ejecucion: '#f59e0b',
    terminado: '#6b7280',
    cancelado: '#ef4444',
  };

  const estadoLabels = {
    nuevo: 'Nuevo',
    presupuesto_venta_en_trabajo: 'Presupuesto en Trabajo',
    presupuesto_venta_en_aprobacion_interna: 'Presupuesto en Aprobación Interna',
    presupuesto_venta_aprobado_internamente: 'Presupuesto Aprobado Internamente',
    presupuesto_venta_enviado_a_cliente: 'Presupuesto Enviado a Cliente',
    presupuesto_venta_aprobado_cliente: 'Presupuesto Aprobado por Cliente',
    en_ejecucion: 'En Ejecución',
    terminado: 'Terminado',
    cancelado: 'Cancelado',
  };

  // Helper function to determine if a tab should be visible based on proyecto type
  const isTabVisible = (tabName) => {
    const tipoProyecto = proyecto.tipo_proyecto?.toLowerCase();

    // Siempre mostrar pestañas generales
    if (['general', 'presupuesto-venta', 'presupuesto-costo', 'ordenes-compra', 'bodega', 'documentos', 'avance-por-gastar', 'gantt', 'tareas'].includes(tabName)) {
      return true;
    }

    // Si tipo_proyecto es "redes_sociales", NO mostrar "workflow-proyectos"
    if (tipoProyecto === 'redes_sociales' && tabName === 'workflow-proyectos') {
      return false;
    }

    // Si tipo_proyecto es "evento", NO mostrar "workflow"
    if (tipoProyecto === 'evento' && tabName === 'workflow') {
      return false;
    }

    return true;
  };

  // Helper function to calculate total gastado
  // Total Gastado = Suma de todas las imputaciones de costo (OCs + Workflow Kanban + Workflow Proyectos)
  const calcularTotalGastado = () => {
    // Sumar todas las imputaciones de costo desde PresupuestoCostoImputacion
    if (detalleCostosGastados && detalleCostosGastados.length > 0) {
      return detalleCostosGastados.reduce((sum, detalle) => {
        return sum + parseFloat(detalle.total || 0);
      }, 0);
    }
    return 0;
  };

  // Cargar detalles de todos los costos gastados desde PresupuestoCostoImputacion
  const cargarDetalleCostosGastados = async () => {
    console.log('🔍 Cargando imputaciones de costo...');
    console.log('📊 Presupuestos de costo:', presupuestosCosto);
    try {
      const detalles = [];

      // Consultar imputaciones para cada presupuesto de costo
      if (presupuestosCosto && presupuestosCosto.length > 0) {
        console.log(`✅ Encontrados ${presupuestosCosto.length} presupuestos de costo`);

        for (const presupuesto of presupuestosCosto) {
          try {
            // Consultar imputaciones filtradas por presupuesto_costo
            const response = await imputacionCostoService.getAll({
              presupuesto_costo: presupuesto.id,
              ordering: 'area__nombre,partida__nombre,recurso__nombre'
            });

            const imputaciones = Array.isArray(response.data) ? response.data : (response.data.results || []);
            console.log(`📦 Imputaciones encontradas para presupuesto ${presupuesto.id}:`, imputaciones.length);

            // Transformar imputaciones al formato esperado por la tabla
            imputaciones.forEach(imp => {
              detalles.push({
                presupuesto: presupuesto.nombre || 'Sin nombre',
                area: imp.area_nombre || 'Sin área',
                partida: imp.partida_nombre || 'Sin partida',
                recurso: imp.recurso_nombre || 'Sin nombre',
                descripcion: imp.notas || '',
                tipo_documento: imp.tipo_documento,
                numero_documento: imp.numero_documento,
                cantidad: imp.cantidad,
                precio_unitario: imp.precio_unitario,
                total: imp.total,
                fecha_imputacion: imp.fecha_imputacion,
                created_by: imp.created_by_nombre
              });
            });
          } catch (error) {
            console.error(`Error al cargar imputaciones del presupuesto ${presupuesto.id}:`, error);
          }
        }
      }

      console.log(`💰 Total de imputaciones encontradas: ${detalles.length}`);
      console.log('📋 Detalles:', detalles);
      setDetalleCostosGastados(detalles);
    } catch (error) {
      console.error('❌ Error loading imputaciones de costo:', error);
    }
  };

  // Calcular total ejecutado de un presupuesto usando el endpoint optimizado
  const calcularTotalEjecutadoPresupuesto = async (presupuestoId) => {
    try {
      const response = await presupuestoCostoService.getTotales(presupuestoId);
      return parseFloat(response.data.total_ejecutado_costo || 0);
    } catch (error) {
      console.error('Error calculating total ejecutado:', error);
      return 0;
    }
  };

  // Calcular total gastado de un presupuesto usando el endpoint optimizado
  const calcularTotalGastadoPresupuesto = async (presupuestoId) => {
    try {
      const response = await presupuestoCostoService.getTotales(presupuestoId);
      return parseFloat(response.data.total_gastado_costo || 0);
    } catch (error) {
      console.error('Error calculating total gastado:', error);
      return 0;
    }
  };

  // Calcular proyección: Costo Esperado + Σ(cantidad_disponible * precio_promedio_OCs)
  const calcularProyeccion = async (presupuestoId) => {
    try {
      let totalProyeccionDisponible = 0;

      // Obtener áreas del presupuesto
      const areasResponse = await presupuestoHierarchyService.getCostoAreasForPresupuesto(presupuestoId);
      const areas = Array.isArray(areasResponse.data) ? areasResponse.data : (areasResponse.data.results || []);

      // Para cada área, obtener partidas y sus recursos
      for (const area of areas) {
        try {
          const partidasResponse = await presupuestoHierarchyService.getCostoPartidas(area.id);
          const partidas = Array.isArray(partidasResponse.data) ? partidasResponse.data : (partidasResponse.data.results || []);

          for (const partida of partidas) {
            try {
              const recursosResponse = await presupuestoHierarchyService.getCostoRecursos(partida.id);
              const recursos = Array.isArray(recursosResponse.data) ? recursosResponse.data : (recursosResponse.data.results || []);

              // Para cada recurso, calcular su proyección
              for (const recurso of recursos) {
                const cantidadDisponible = parseFloat(recurso.cantidad_disponible || 0);

                if (cantidadDisponible > 0) {
                  // Obtener detalles de OC para este recurso
                  // Necesitamos calcular el precio promedio de las OCs del recurso
                  // Como no tenemos un endpoint específico, usaremos el precio_unitario_costo del recurso
                  const precioUnitario = parseFloat(recurso.precio_unitario_costo || 0);
                  totalProyeccionDisponible += cantidadDisponible * precioUnitario;
                }
              }
            } catch (error) {
              console.error('Error obtaining recursos for partida:', error);
            }
          }
        } catch (error) {
          console.error('Error obtaining partidas for area:', error);
        }
      }

      return totalProyeccionDisponible;
    } catch (error) {
      console.error('Error calculating proyección:', error);
      return 0;
    }
  };

  // Estados para presupuestos
  const [presupuestosVenta, setPresupuestosVenta] = useState([]);
  const [presupuestosCosto, setPresupuestosCosto] = useState([]);
  const [totalEjecutadoPresupuestos, setTotalEjecutadoPresupuestos] = useState({}); // Almacenar totales ejecutados por presupuesto
  const [totalGastadoPresupuestos, setTotalGastadoPresupuestos] = useState({}); // Almacenar totales gastados por presupuesto
  const [totalGastadoProyecto, setTotalGastadoProyecto] = useState(0); // Almacenar total gastado del proyecto
  const [proyeccionPresupuestos, setProyeccionPresupuestos] = useState({}); // Almacenar proyecciones por presupuesto

  // Estados para recursos del proyecto
  const [recursosProyecto, setRecursosProyecto] = useState([]);
  const [loadingRecursos, setLoadingRecursos] = useState(false);

  // Estados para órdenes de compra
  const [ordenesCompra, setOrdenesCompra] = useState([]);

  // Estados para movimientos de bodega
  const [movimientosBodega, setMovimientosBodega] = useState([]);
  const [showCrearMovimientoBodega, setShowCrearMovimientoBodega] = useState(false);
  const [movimientoEditando, setMovimientoEditando] = useState(null);
  const [filtrosBodega, setFiltrosBodega] = useState({
    bodega: '',
    tipo_movimiento: '',
    motivo: '',
    tipo_documento: ''
  });

  // Estados para items expandidos
  const [presupuestoExpandidoId, setPresupuestoExpandidoId] = useState(null);
  const [itemsExpandidos, setItemsExpandidos] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Estados para crear/editar presupuesto
  const [showCrearPresupuestoVenta, setShowCrearPresupuestoVenta] = useState(false);
  const [showCrearPresupuestoCosto, setShowCrearPresupuestoCosto] = useState(false);
  const [presupuestoEditando, setPresupuestoEditando] = useState(null);
  const [tipoPresupuestoEditando, setTipoPresupuestoEditando] = useState(null); // 'venta' o 'costo'
  const [formDataPresupuesto, setFormDataPresupuesto] = useState({
    nombre: '',
    descripcion: '',
    comision_agencia_porcentaje: 0,
    descuento_porcentaje: 0,
    aplicar_iva: true,
    iva_porcentaje: 19,
  });

  // Estados para agregar items
  const [showAgregarItemVenta, setShowAgregarItemVenta] = useState(false);
  const [showAgregarItemCosto, setShowAgregarItemCosto] = useState(false);
  const [presupuestoSeleccionado, setPresupuestoSeleccionado] = useState(null);
  const [itemParaEditar, setItemParaEditar] = useState(null);

  // Estados para selección de items y OC
  const [modoSeleccion, setModoSeleccion] = useState(false);
  const [itemsSeleccionados, setItemsSeleccionados] = useState([]);
  const [showCrearOrdenCompra, setShowCrearOrdenCompra] = useState(false);
  const [ordenCompraEditando, setOrdenCompraEditando] = useState(null);
  const [showVerEditarOC, setShowVerEditarOC] = useState(false);
  const [ordenCompraParaVer, setOrdenCompraParaVer] = useState(null);

  // Estados para seleccionar items y crear presupuesto de costos
  const [showSeleccionarItemsModal, setShowSeleccionarItemsModal] = useState(false);
  const [presupuestoParaCostos, setPresupuestoParaCostos] = useState(null);

  // Estados para Avance por Gastar
  const [resumenEjecucion, setResumenEjecucion] = useState(null);
  const [itemsPorEstado, setItemsPorEstado] = useState(null);
  const [loadingAvance, setLoadingAvance] = useState(false);
  const [detalleCostosGastados, setDetalleCostosGastados] = useState([]);
  const [showModalDetalleCostos, setShowModalDetalleCostos] = useState(false);

  // Estados para editar Información General
  const [editarInformacionGeneral, setEditarInformacionGeneral] = useState(false);
  const [formDataProyecto, setFormDataProyecto] = useState({
    nombre: '',
    tipo_proyecto: '',
    cliente: '',
    linea_negocio: '',
    fecha_cotizacion: '',
    margen_objetivo: '',
    fecha_inicio_planeado: '',
    fecha_termino_planeada: ''
  });

  // Estados para clientes y líneas de negocio
  const [clientes, setClientes] = useState([]);
  const [lineasNegocio, setLineasNegocio] = useState([]);

  // Estado local para el proyecto (para reflejar cambios sin recargar)
  const [proyectoLocal, setProyectoLocal] = useState(proyecto);

  // Sincronizar proyectoLocal cuando cambia la prop proyecto
  useEffect(() => {
    setProyectoLocal(proyecto);
  }, [proyecto.id]);

  // Estados para Rendiciones
  const [rendiciones, setRendiciones] = useState([]);
  const [showCrearRendicion, setShowCrearRendicion] = useState(false);
  const [rendicionEditando, setRendicionEditando] = useState(null);
  const [rendicionExpandida, setRendicionExpandida] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [formDataRendicion, setFormDataRendicion] = useState({
    orden_compra: '',
    proveedor: '',
    numero_comprobante: '',
    tipo_comprobante: 'factura',
    fecha_comprobante: new Date().toISOString().split('T')[0],
    descripcion: '',
    estado_aprobacion: 'pendiente_aprobacion',
  });
  const [detallesRendicion, setDetallesRendicion] = useState([]);

  // Estados para Eventos
  const [eventos, setEventos] = useState([]);

  useEffect(() => {
    if (activeTab === 'presupuesto-venta') {
      cargarPresupuestosVenta();
    } else if (activeTab === 'presupuesto-costo') {
      cargarPresupuestosCosto();
      cargarEventos();
    } else if (activeTab === 'ordenes-compra') {
      cargarOrdenesCompra();
    } else if (activeTab === 'bodega') {
      cargarMovimientosBodega();
    } else if (activeTab === 'avance-por-gastar') {
      cargarAvancePorGastar();
      cargarEventos();
    } else if (activeTab === 'workflow') {
      cargarEventos();
    } else if (activeTab === 'workflow-proyectos') {
      // No need to load eventos separately - data comes from proyecto.costo_workflow_por_evento
    } else if (activeTab === 'gantt') {
      cargarEventos();
    } else if (activeTab === 'documentos') {
      cargarRendiciones();
      cargarProveedores();
      cargarCategorias();
    }
  }, [activeTab, proyecto.id]);

  // Cargar presupuestos de venta y costo al iniciar para tener datos actualizados en la pestaña General
  useEffect(() => {
    cargarPresupuestosVenta();
    cargarPresupuestosCosto();
  }, [proyecto.id]);

  // Si la pestaña actual no es visible para el tipo de proyecto actual, cambiar a 'general'
  useEffect(() => {
    if (!isTabVisible(activeTab)) {
      setActiveTab('general');
    }
  }, [proyecto.tipo_proyecto]);

  // Calcular totales ejecutados y proyección para cada presupuesto de costo
  useEffect(() => {
    const calcularTotales = async () => {
      const totales = {};
      const proyecciones = {};
      for (const presupuesto of presupuestosCosto) {
        const totalEjecutado = await calcularTotalEjecutadoPresupuesto(presupuesto.id);
        const proyeccionDisponible = await calcularProyeccion(presupuesto.id);
        totales[presupuesto.id] = totalEjecutado;
        proyecciones[presupuesto.id] = proyeccionDisponible;
      }
      setTotalEjecutadoPresupuestos(totales);
      setProyeccionPresupuestos(proyecciones);
    };

    if (presupuestosCosto.length > 0) {
      calcularTotales();
    }
  }, [presupuestosCosto]);

  // Calcular totales gastados por presupuesto
  useEffect(() => {
    const calcularTotalesGastados = async () => {
      const totales = {};
      for (const presupuesto of presupuestosCosto) {
        const totalGastado = await calcularTotalGastadoPresupuesto(presupuesto.id);
        totales[presupuesto.id] = totalGastado;
      }
      setTotalGastadoPresupuestos(totales);
    };

    if (presupuestosCosto.length > 0) {
      calcularTotalesGastados();
    }
  }, [presupuestosCosto]);

  // Cargar detalle de costos gastados cuando se está en la pestaña de Avance por Gastar o Presupuesto Costo
  useEffect(() => {
    console.log('🔄 useEffect detalle costos - activeTab:', activeTab, 'presupuestosCosto.length:', presupuestosCosto.length);
    if ((activeTab === 'avance-por-gastar' || activeTab === 'presupuesto-costo') && presupuestosCosto.length > 0) {
      console.log('✅ Llamando a cargarDetalleCostosGastados...');
      cargarDetalleCostosGastados();
    }
  }, [activeTab, presupuestosCosto]);

  // Cargar recursos del proyecto cuando se está en la pestaña de Recursos Solicitados
  useEffect(() => {
    if (activeTab === 'recursos-solicitados' && recursosProyecto.length === 0) {
      cargarRecursosProyecto();
    }
  }, [activeTab]);

  // Calcular total gastado del proyecto sumando todos los presupuestos
  useEffect(() => {
    // Sumar todos los totales gastados de cada presupuesto
    const total = Object.values(totalGastadoPresupuestos).reduce((sum, valor) => sum + valor, 0);
    setTotalGastadoProyecto(total);
  }, [totalGastadoPresupuestos]);

  const cargarPresupuestosVenta = async () => {
    setLoading(true);
    try {
      const response = await presupuestoVentaService.getAll({
        proyecto: proyecto.id,
        status: 'activo'
      });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setPresupuestosVenta(data);
    } catch (error) {
      console.error('Error al cargar presupuestos de venta:', error);
      toast.error('Error al cargar presupuestos de venta');
    } finally {
      setLoading(false);
    }
  };

  const cargarPresupuestosCosto = async () => {
    setLoading(true);
    try {
      const response = await presupuestoCostoService.getAll({
        proyecto: proyecto.id,
        status: 'activo'
      });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setPresupuestosCosto(data);
    } catch (error) {
      console.error('Error al cargar presupuestos de costo:', error);
      toast.error('Error al cargar presupuestos de costo');
    } finally {
      setLoading(false);
    }
  };

  const cargarOrdenesCompra = async () => {
    setLoading(true);
    try {
      const response = await ordenCompraService.getAll({
        proyecto: proyecto.id,
        status: 'activo'
      });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setOrdenesCompra(data);
    } catch (error) {
      console.error('Error al cargar órdenes de compra:', error);
      toast.error('Error al cargar órdenes de compra');
    } finally {
      setLoading(false);
    }
  };

  const cargarMovimientosBodega = async () => {
    setLoading(true);
    try {
      const response = await bodegaService.getMovimientos();
      const todosMovimientos = Array.isArray(response.data) ? response.data : (response.data.results || []);

      // Filtrar movimientos asociados a este proyecto (por cuenta)
      const movimientosProyecto = todosMovimientos.filter(mov =>
        mov.cuenta === proyecto.cuenta &&
        mov.status === 'activo'
      );

      setMovimientosBodega(movimientosProyecto);
    } catch (error) {
      console.error('Error al cargar movimientos de bodega:', error);
      toast.error('Error al cargar movimientos de bodega');
    } finally {
      setLoading(false);
    }
  };

  const cargarRecursosProyecto = async () => {
    setLoadingRecursos(true);
    try {
      const todosRecursos = [];

      // Obtener todos los presupuestos de costo del proyecto
      const presupuestosResponse = await presupuestoCostoService.getAll({
        proyecto: proyecto.id,
        status: 'activo'
      });
      const presupuestos = Array.isArray(presupuestosResponse.data) ? presupuestosResponse.data : (presupuestosResponse.data.results || []);

      // Para cada presupuesto, obtener sus áreas, partidas y recursos
      for (const presupuesto of presupuestos) {
        try {
          // Obtener áreas del presupuesto
          const areasResponse = await presupuestoHierarchyService.getCostoAreasForPresupuesto(presupuesto.id);
          const areas = Array.isArray(areasResponse.data) ? areasResponse.data : (areasResponse.data.results || []);

          // Para cada área, obtener sus partidas
          for (const area of areas) {
            try {
              const partidasResponse = await presupuestoHierarchyService.getCostoPartidas(area.id);
              const partidas = Array.isArray(partidasResponse.data) ? partidasResponse.data : (partidasResponse.data.results || []);

              // Para cada partida, obtener sus recursos
              for (const partida of partidas) {
                try {
                  const recursosResponse = await presupuestoHierarchyService.getCostoRecursos(partida.id);
                  const recursos = Array.isArray(recursosResponse.data) ? recursosResponse.data : (recursosResponse.data.results || []);

                  // Agregar información de contexto a cada recurso
                  recursos.forEach(recurso => {
                    todosRecursos.push({
                      ...recurso,
                      area_nombre: area.nombre,
                      partida_nombre: partida.nombre,
                      presupuesto_nombre: presupuesto.nombre
                    });
                  });
                } catch (error) {
                  console.error(`Error al cargar recursos de partida ${partida.id}:`, error);
                }
              }
            } catch (error) {
              console.error(`Error al cargar partidas de área ${area.id}:`, error);
            }
          }
        } catch (error) {
          console.error(`Error al cargar áreas de presupuesto ${presupuesto.id}:`, error);
        }
      }

      setRecursosProyecto(todosRecursos);
    } catch (error) {
      console.error('Error al cargar recursos del proyecto:', error);
      toast.error('Error al cargar recursos del proyecto');
    } finally {
      setLoadingRecursos(false);
    }
  };

  const cargarEventos = async () => {
    try {
      const response = await eventosService.getEventos({
        proyecto: proyecto.id
      });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setEventos(data);
    } catch (error) {
      console.error('Error al cargar eventos:', error);
      // No mostramos error toast aquí ya que eventos es opcional
    }
  };

  const cargarRendiciones = async () => {
    setLoading(true);
    try {
      const response = await rendicionService.getAll({
        proyecto: proyecto.id,
        status: 'activo'
      });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setRendiciones(data);
    } catch (error) {
      console.error('Error al cargar rendiciones:', error);
      toast.error('Error al cargar rendiciones');
    } finally {
      setLoading(false);
    }
  };

  const cargarProveedores = async () => {
    try {
      const response = await proveedorService.getAll({ status: 'activo' });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setProveedores(data);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  const cargarCategorias = async () => {
    try {
      const response = await categoriaPresupuestoService.getAll({ status: 'activo' });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setCategorias(data);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  const cargarClientes = async () => {
    try {
      const response = await clienteService.getAll();
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setClientes(data);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      toast.error('Error al cargar clientes');
    }
  };

  const cargarLineasNegocio = async () => {
    try {
      const response = await lineaNegocioService.getAll();
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setLineasNegocio(data);
    } catch (error) {
      console.error('Error al cargar líneas de negocio:', error);
      toast.error('Error al cargar líneas de negocio');
    }
  };

  const cargarAvancePorGastar = async () => {
    setLoadingAvance(true);
    try {
      // Buscar el presupuesto de venta activo del proyecto (para items por estado)
      const presupuestosVentaResponse = await presupuestoVentaService.getAll({
        proyecto: proyecto.id,
        status: 'activo'
      });
      const presupuestosVenta = Array.isArray(presupuestosVentaResponse.data)
        ? presupuestosVentaResponse.data
        : (presupuestosVentaResponse.data.results || []);

      // Buscar el presupuesto de costo activo del proyecto (para totales)
      const presupuestosCostoResponse = await presupuestoCostoService.getAll({
        proyecto: proyecto.id,
        status: 'activo'
      });
      const presupuestosCosto = Array.isArray(presupuestosCostoResponse.data)
        ? presupuestosCostoResponse.data
        : (presupuestosCostoResponse.data.results || []);

      if (presupuestosVenta.length === 0) {
        setResumenEjecucion(null);
        setItemsPorEstado(null);
        return;
      }

      // Tomar el primer presupuesto activo de venta
      const presupuestoVenta = presupuestosVenta[0];

      // Cargar resumen de ejecución del presupuesto de venta
      const resumenResponse = await presupuestoVentaService.getResumenEjecucion(presupuestoVenta.id);
      const resumenVenta = resumenResponse.data;

      // Si hay presupuesto de costo, usar sus totales
      let resumenFinal = resumenVenta;
      if (presupuestosCosto.length > 0) {
        const presupuestoCosto = presupuestosCosto[0];
        // Calcular total ejecutado: costo_unitario_esperado * cantidad_ejecutada
        const totalEjecutado = parseFloat(presupuestoCosto.costo_ejecutado_esperado) || 0;
        const totalPresupuestado = parseFloat(presupuestoCosto.total_presupuestado) || 0;

        // Reemplazar totales con los del presupuesto de costo
        resumenFinal = {
          ...resumenVenta,
          total_presupuestado: totalPresupuestado,
          total_ejecutado: totalEjecutado,
          total_pendiente: totalPresupuestado - totalEjecutado
        };
      }

      setResumenEjecucion(resumenFinal);

      // Cargar items por estado
      const itemsResponse = await presupuestoVentaService.getItemsPorEstado(presupuestoVenta.id);
      console.log('Items por estado recibidos:', itemsResponse.data);
      setItemsPorEstado(itemsResponse.data);
    } catch (error) {
      console.error('Error al cargar avance por gastar:', error);
      toast.error('Error al cargar información de ejecución');
    } finally {
      setLoadingAvance(false);
    }
  };

  const handleVerItems = async (presupuesto, tipo) => {
    // Si es el mismo presupuesto, colapsar
    if (presupuestoExpandidoId === presupuesto.id) {
      setPresupuestoExpandidoId(null);
      setItemsExpandidos([]);
      setPresupuestoSeleccionado(null);
      return;
    }

    // Expandir y cargar items
    setPresupuestoExpandidoId(presupuesto.id);
    setPresupuestoSeleccionado(presupuesto);
    setLoadingItems(true);

    try {
      const service = tipo === 'venta' ? itemPresupuestoVentaService : itemPresupuestoCostoService;
      const response = await service.getAll({
        presupuesto_venta: tipo === 'venta' ? presupuesto.id : undefined,
        presupuesto_costo: tipo === 'costo' ? presupuesto.id : undefined,
        status: 'activo'
      });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setItemsExpandidos(data);
    } catch (error) {
      console.error('Error al cargar items:', error);
      toast.error('Error al cargar items');
      setItemsExpandidos([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleEditarPresupuestoVenta = (presupuesto) => {
    setPresupuestoEditando(presupuesto);
    setTipoPresupuestoEditando('venta');
    setFormDataPresupuesto({
      nombre: presupuesto.nombre || '',
      descripcion: presupuesto.descripcion || '',
      comision_agencia_porcentaje: presupuesto.comision_agencia_porcentaje || 0,
      descuento_porcentaje: presupuesto.descuento_porcentaje || 0,
      aplicar_iva: presupuesto.aplicar_iva !== false,
      iva_porcentaje: presupuesto.iva_porcentaje || 19,
    });
    setShowCrearPresupuestoVenta(true);
  };

  const handleEditarPresupuestoCosto = (presupuesto) => {
    setPresupuestoEditando(presupuesto);
    setTipoPresupuestoEditando('costo');
    setFormDataPresupuesto({
      nombre: presupuesto.nombre || '',
      descripcion: presupuesto.descripcion || '',
      comision_agencia_porcentaje: presupuesto.comision_agencia_porcentaje || 0,
      descuento_porcentaje: 0,
      aplicar_iva: true,
      iva_porcentaje: 19,
    });
    setShowCrearPresupuestoCosto(true);
  };

  const handleCrearPresupuestoVenta = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formDataPresupuesto,
        proyecto: proyecto.id,
      };

      if (presupuestoEditando) {
        // Editar
        await presupuestoVentaService.update(presupuestoEditando.id, data);
        toast.success('Presupuesto de venta actualizado');
      } else {
        // Crear
        await presupuestoVentaService.create(data);
        toast.success('Presupuesto de venta creado');
      }

      setShowCrearPresupuestoVenta(false);
      setPresupuestoEditando(null);
      setTipoPresupuestoEditando(null);
      resetFormPresupuesto();
      cargarPresupuestosVenta();
    } catch (error) {
      console.error('Error:', error);
      toast.error(presupuestoEditando ? 'Error al actualizar presupuesto de venta' : 'Error al crear presupuesto de venta');
    }
  };

  const handleCrearPresupuestoCosto = async (e) => {
    e.preventDefault();
    try {
      const data = {
        nombre: formDataPresupuesto.nombre,
        descripcion: formDataPresupuesto.descripcion,
        comision_agencia_porcentaje: formDataPresupuesto.comision_agencia_porcentaje,
        proyecto: proyecto.id,
      };

      if (presupuestoEditando) {
        // Editar
        await presupuestoCostoService.update(presupuestoEditando.id, data);
        toast.success('Presupuesto de costo actualizado');
      } else {
        // Crear
        await presupuestoCostoService.create(data);
        toast.success('Presupuesto de costo creado');
      }

      setShowCrearPresupuestoCosto(false);
      setPresupuestoEditando(null);
      setTipoPresupuestoEditando(null);
      resetFormPresupuesto();
      cargarPresupuestosCosto();
    } catch (error) {
      console.error('Error:', error);
      toast.error(presupuestoEditando ? 'Error al actualizar presupuesto de costo' : 'Error al crear presupuesto de costo');
    }
  };

  const resetFormPresupuesto = () => {
    setFormDataPresupuesto({
      nombre: '',
      descripcion: '',
      comision_agencia_porcentaje: 0,
      descuento_porcentaje: 0,
      aplicar_iva: true,
      iva_porcentaje: 19,
    });
    setPresupuestoEditando(null);
    setTipoPresupuestoEditando(null);
  };

  const handleInputChangePresupuesto = (e) => {
    const { name, value, type, checked } = e.target;
    setFormDataPresupuesto((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAgregarItemVenta = (presupuesto) => {
    setPresupuestoSeleccionado(presupuesto);
    setShowAgregarItemVenta(true);
  };

  const handleDuplicarPresupuestoVenta = async (presupuesto) => {
    if (!window.confirm('¿Desea duplicar este presupuesto de venta? Se creará una copia en estado borrador.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await presupuestoVentaService.duplicar(presupuesto.id);
      toast.success('Presupuesto duplicado exitosamente');
      // Recargar presupuestos para mostrar el duplicado
      await cargarPresupuestosVenta();
    } catch (error) {
      console.error('Error al duplicar presupuesto:', error);
      toast.error('Error al duplicar el presupuesto');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarPresupuestoVenta = async (presupuesto) => {
    if (!window.confirm(`¿Está seguro de eliminar el Presupuesto de Venta "${presupuesto.nombre || 'Sin nombre'}"? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      setLoading(true);
      await presupuestoVentaService.delete(presupuesto.id);
      toast.success('Presupuesto de venta eliminado correctamente');
      // Recargar presupuestos de venta
      await cargarPresupuestosVenta();
    } catch (error) {
      console.error('Error al eliminar presupuesto de venta:', error);
      toast.error('Error al eliminar presupuesto de venta');
    } finally {
      setLoading(false);
    }
  };

  const handleAgregarItemCosto = (presupuesto) => {
    setPresupuestoSeleccionado(presupuesto);
    setShowAgregarItemCosto(true);
  };

  const handleEditarItemVenta = (item) => {
    // Configurar el item para editar y abrir el modal
    setItemParaEditar(item);
    setShowAgregarItemVenta(true);
  };

  const handleEliminarItemVenta = async (itemId) => {
    if (!window.confirm('¿Está seguro de eliminar este item?')) {
      return;
    }

    try {
      await itemPresupuestoVentaService.delete(itemId);
      toast.success('Item eliminado correctamente');
      await cargarPresupuestosVenta();
      // Recargar items si hay un presupuesto expandido
      if (presupuestoExpandidoId && presupuestoSeleccionado) {
        await handleVerItems(presupuestoSeleccionado, 'venta');
      }
    } catch (error) {
      console.error('Error al eliminar item:', error);
      toast.error('Error al eliminar item');
    }
  };

  const handleItemAdded = async () => {
    // Recargar presupuestos según el tab activo
    if (activeTab === 'presupuesto-venta') {
      await cargarPresupuestosVenta();
    } else if (activeTab === 'presupuesto-costo') {
      await cargarPresupuestosCosto();
    }

    // Si hay items expandidos, recargarlos directamente sin toggle
    if (presupuestoExpandidoId) {
      setLoadingItems(true);
      try {
        const service = activeTab === 'presupuesto-venta' ? itemPresupuestoVentaService : itemPresupuestoCostoService;
        const response = await service.getAll({
          presupuesto_venta: activeTab === 'presupuesto-venta' ? presupuestoExpandidoId : undefined,
          presupuesto_costo: activeTab === 'presupuesto-costo' ? presupuestoExpandidoId : undefined,
          status: 'activo'
        });
        const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
        setItemsExpandidos(data);
      } catch (error) {
        console.error('Error al cargar items:', error);
        toast.error('Error al cargar items');
      } finally {
        setLoadingItems(false);
      }
    }
  };

  // Funciones para manejo de selección de items
  const handleToggleModoSeleccion = () => {
    setModoSeleccion(!modoSeleccion);
    setItemsSeleccionados([]);
  };

  const handleToggleItemSeleccion = (itemId) => {
    setItemsSeleccionados(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleSolicitarOC = () => {
    setShowCrearOrdenCompra(true);
  };

  const handleOrdenCompraCreada = async () => {
    setShowCrearOrdenCompra(false);
    setModoSeleccion(false);
    setItemsSeleccionados([]);
    setOrdenCompraEditando(null);

    // Recargar presupuestos y items
    await cargarPresupuestosCosto();
    await cargarPresupuestosVenta();
    await cargarOrdenesCompra();
    await cargarAvancePorGastar();

    if (presupuestoExpandidoId && presupuestoSeleccionado) {
      await handleVerItems(presupuestoSeleccionado, 'costo');
    }

    // Notificar al componente padre para que recargue el proyecto
    if (onUpdate) {
      onUpdate();
    }
  };

  // Workflow handlers
  const handleEnviarAprobacion = async (presupuesto) => {
    try {
      await presupuestoVentaService.update(presupuesto.id, { estado: 'enviado_aprobacion_interna' });
      toast.success('Presupuesto enviado a aprobación interna');
      cargarPresupuestosVenta();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al enviar a aprobación');
    }
  };

  const handleAprobar = async (presupuesto) => {
    try {
      await presupuestoVentaService.update(presupuesto.id, { estado: 'aprobacion_interna' });
      toast.success('Presupuesto aprobado internamente');
      cargarPresupuestosVenta();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al aprobar presupuesto');
    }
  };

  const handleRechazar = async (presupuesto) => {
    if (window.confirm('¿Rechazar este presupuesto internamente?')) {
      try {
        await presupuestoVentaService.update(presupuesto.id, { estado: 'rechazo_interno' });
        toast.success('Presupuesto rechazado internamente');
        cargarPresupuestosVenta();
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al rechazar presupuesto');
      }
    }
  };

  const handleEnviarAprobacionCliente = async (presupuesto) => {
    try {
      await presupuestoVentaService.update(presupuesto.id, { estado: 'enviado_aprobacion_cliente' });
      toast.success('Presupuesto enviado a aprobación del cliente');
      cargarPresupuestosVenta();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al enviar a aprobación del cliente');
    }
  };

  const handleReintentarAprobacionInterna = async (presupuesto) => {
    try {
      await presupuestoVentaService.update(presupuesto.id, { estado: 'enviado_aprobacion_interna' });
      toast.success('Presupuesto reenviado a aprobación interna');
      cargarPresupuestosVenta();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al reintentar aprobación');
    }
  };

  const handleAprobarCliente = async (presupuesto) => {
    try {
      await presupuestoVentaService.update(presupuesto.id, { estado: 'aprobacion_cliente' });
      toast.success('Presupuesto aprobado por cliente');
      cargarPresupuestosVenta();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al aprobar presupuesto del cliente');
    }
  };

  const handleRechazarCliente = async (presupuesto) => {
    if (window.confirm('¿Rechazar este presupuesto del cliente?')) {
      try {
        await presupuestoVentaService.update(presupuesto.id, { estado: 'rechazo_cliente' });
        toast.success('Presupuesto rechazado por cliente');
        cargarPresupuestosVenta();
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al rechazar presupuesto del cliente');
      }
    }
  };

  const handleAbrirSeleccionItems = (presupuesto) => {
    setPresupuestoVentaParaCosto(presupuesto);
    setAsignarPresupuestoCostoModalOpen(true);
  };

  const handleAsignarAreas = (presupuesto, tipo) => {
    setPresupuestoParaAsignarAreas(presupuesto);
    setTipoPresupuestoParaAsignarAreas(tipo);
    setAsignarAreasModalOpen(true);
  };

  const handleCrearPresupuestoCostos = async (itemsSeleccionados) => {
    try {
      if (!presupuestoParaCostos) {
        toast.error('No se encontró el presupuesto de venta');
        return;
      }

      // 1. Crear presupuesto de costos
      const nuevoPresupuestoCosto = {
        proyecto: presupuestoParaCostos.proyecto,
        estado: 'borrador',
        descripcion: `Presupuesto de costos para: ${presupuestoParaCostos.descripcion || presupuestoParaCostos.proyecto_nombre}`,
      };

      const presupuestoCostoResponse = await presupuestoCostoService.create(nuevoPresupuestoCosto);
      const presupuestoCostoCreado = presupuestoCostoResponse.data;

      // 2. Crear items del presupuesto de costos solo con los items seleccionados
      if (itemsSeleccionados.length > 0) {
        for (const item of itemsSeleccionados) {
          const nuevoItemCosto = {
            presupuesto_costo: presupuestoCostoCreado.id,
            categoria: item.categoria,
            descripcion: item.descripcion,
            cantidad: item.cantidad_seleccionada || item.cantidad, // Usar cantidad seleccionada
            cantidad_dias: item.cantidad_dias,
            unidad: item.unidad,
            costo_unitario: item.costo_unitario || 0,
            status: 'activo',
          };

          await itemPresupuestoCostoService.create(nuevoItemCosto);
        }
      }

      toast.success(`Presupuesto de costos creado con ${itemsSeleccionados.length} items`);

      // 3. Verificar si quedan cantidades disponibles
      const quedanCantidadesDisponibles = await verificarCantidadesDisponibles(presupuestoParaCostos.id);

      // 4. Cambiar estado según disponibilidad
      if (!quedanCantidadesDisponibles) {
        // Si no quedan cantidades disponibles, marcar como terminado
        await presupuestoVentaService.update(presupuestoParaCostos.id, { estado: 'terminado' });
        toast.info('Presupuesto de venta marcado como terminado - todas las cantidades han sido asignadas');
      }
      // Si quedan cantidades, mantener el estado en 'aprobacion_cliente'

      // 5. Cerrar modal y limpiar estados
      setShowSeleccionarItemsModal(false);
      setPresupuestoParaCostos(null);

      cargarPresupuestosVenta();
      cargarPresupuestosCosto();
      setActiveTab('presupuesto-costo'); // Cambiar a pestaña de costos
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al crear presupuesto de costos');
    }
  };

  const verificarCantidadesDisponibles = async (presupuestoVentaId) => {
    try {
      // 1. Obtener todos los items del presupuesto de venta
      const itemsVentaResponse = await itemPresupuestoVentaService.getAll({
        presupuesto_venta: presupuestoVentaId,
        status: 'activo',
        limit: 1000
      });
      const itemsVenta = Array.isArray(itemsVentaResponse.data)
        ? itemsVentaResponse.data
        : (itemsVentaResponse.data.results || []);

      if (itemsVenta.length === 0) return false;

      // 2. Obtener el proyecto del presupuesto de venta
      const presupuestoVenta = presupuestosVenta.find(p => p.id === presupuestoVentaId) || presupuestoParaCostos;
      if (!presupuestoVenta) return false;

      // 3. Obtener todos los presupuestos de costos del proyecto
      const presupuestosCostoResponse = await presupuestoCostoService.getAll({
        proyecto: presupuestoVenta.proyecto,
        status: 'activo',
        limit: 1000
      });
      const presupuestosCostoList = Array.isArray(presupuestosCostoResponse.data)
        ? presupuestosCostoResponse.data
        : (presupuestosCostoResponse.data.results || []);

      // 4. Calcular cantidades asignadas por descripción
      const cantidadesAsignadas = {};
      for (const presupuestoCosto of presupuestosCostoList) {
        const itemsCostoResponse = await itemPresupuestoCostoService.getAll({
          presupuesto_costo: presupuestoCosto.id,
          status: 'activo',
          limit: 1000
        });
        const itemsCosto = Array.isArray(itemsCostoResponse.data)
          ? itemsCostoResponse.data
          : (itemsCostoResponse.data.results || []);

        itemsCosto.forEach(itemCosto => {
          const descripcion = itemCosto.descripcion;
          if (!cantidadesAsignadas[descripcion]) {
            cantidadesAsignadas[descripcion] = 0;
          }
          cantidadesAsignadas[descripcion] += parseFloat(itemCosto.cantidad) || 0;
        });
      }

      // 5. Verificar si algún item tiene cantidad disponible
      for (const itemVenta of itemsVenta) {
        const cantidadTotal = parseFloat(itemVenta.cantidad) || 0;
        const cantidadAsignada = cantidadesAsignadas[itemVenta.descripcion] || 0;
        const disponible = cantidadTotal - cantidadAsignada;

        if (disponible > 0.01) { // Margen de tolerancia para decimales
          return true; // Aún quedan cantidades disponibles
        }
      }

      return false; // No quedan cantidades disponibles
    } catch (error) {
      console.error('Error al verificar cantidades disponibles:', error);
      return true; // En caso de error, asumir que quedan cantidades disponibles
    }
  };

  const handleCambiarEstadoOrden = async (ordenId, nuevoEstado) => {
    try {
      await ordenCompraService.update(ordenId, { estado: nuevoEstado });
      toast.success('Estado de orden actualizado');
      cargarOrdenesCompra();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      toast.error('Error al cambiar estado de la orden');
    }
  };

  const handleEliminarOrdenCompra = async (orden) => {
    if (!window.confirm(`¿Estás seguro de eliminar la Orden de Compra ${orden.numero_oc}?\n\nEsto revertirá las cantidades ejecutadas en el presupuesto de costos.`)) {
      return;
    }

    try {
      // Primero obtener los detalles completos de la OC
      const response = await ordenCompraService.getById(orden.id);
      const ordenCompleta = response.data;

      // Revertir las cantidades reales en los items del presupuesto de costo
      if (ordenCompleta.detalles && ordenCompleta.detalles.length > 0) {
        for (const detalle of ordenCompleta.detalles) {
          // Buscar el item en el presupuesto de costo por descripción
          const presupuestosCosto = await presupuestoCostoService.getAll({
            proyecto: proyecto.id,
            status: 'activo'
          });

          const presupuestosCostoData = Array.isArray(presupuestosCosto.data)
            ? presupuestosCosto.data
            : (presupuestosCosto.data.results || []);

          if (presupuestosCostoData.length > 0) {
            const presupuestoCosto = presupuestosCostoData[0];

            // Obtener items del presupuesto de costo
            const itemsResponse = await itemPresupuestoCostoService.getAll({
              presupuesto_costo: presupuestoCosto.id,
              status: 'activo'
            });

            const items = Array.isArray(itemsResponse.data)
              ? itemsResponse.data
              : (itemsResponse.data.results || []);

            // Buscar el item que coincida con la descripción
            const itemMatch = items.find(item => item.descripcion === detalle.descripcion);

            if (itemMatch) {
              // Obtener valores actuales
              const cantidadRealActual = parseFloat(itemMatch.cantidad_real) || 0;
              const diasRealActual = parseFloat(itemMatch.dias_real) || 0;
              const precioUnitarioRealActual = parseFloat(itemMatch.precio_unitario_real) || 0;

              // Valores del detalle a revertir
              const cantidadDetalle = parseFloat(detalle.cantidad) || 0;
              const diasDetalle = parseFloat(detalle.cantidad_dias) || 1;
              const precioUnitarioDetalle = parseFloat(detalle.precio_unitario) || 0;

              // Calcular nuevos valores
              const nuevaCantidadReal = Math.max(0, cantidadRealActual - cantidadDetalle);
              const nuevoDiasReal = Math.max(0, diasRealActual - diasDetalle);

              // Recalcular precio unitario real (promedio ponderado inverso)
              const totalActual = cantidadRealActual * diasRealActual * precioUnitarioRealActual;
              const totalDetalle = cantidadDetalle * diasDetalle * precioUnitarioDetalle;
              const totalNuevo = totalActual - totalDetalle;
              const cantidadTotalNueva = nuevaCantidadReal * nuevoDiasReal;
              const nuevoPrecioUnitarioReal = cantidadTotalNueva > 0
                ? totalNuevo / cantidadTotalNueva
                : 0;

              await itemPresupuestoCostoService.update(itemMatch.id, {
                cantidad_real: nuevaCantidadReal,
                dias_real: nuevoDiasReal,
                precio_unitario_real: nuevoPrecioUnitarioReal
              });

              // Sincronizar cantidad_ejecutada en el presupuesto de venta
              try {
                // Obtener presupuesto de venta activo del proyecto
                const presupuestosVentaResponse = await presupuestoVentaService.getAll({
                  proyecto: proyecto.id,
                  status: 'activo'
                });
                const presupuestosVenta = Array.isArray(presupuestosVentaResponse.data)
                  ? presupuestosVentaResponse.data
                  : (presupuestosVentaResponse.data.results || []);

                if (presupuestosVenta.length > 0) {
                  const presupuestoVenta = presupuestosVenta[0];

                  // Obtener items del presupuesto de venta
                  const itemsVentaResponse = await itemPresupuestoVentaService.getAll({
                    presupuesto_venta: presupuestoVenta.id,
                    status: 'activo'
                  });
                  const itemsVenta = Array.isArray(itemsVentaResponse.data)
                    ? itemsVentaResponse.data
                    : (itemsVentaResponse.data.results || []);

                  // Buscar item con la misma descripción
                  const itemVenta = itemsVenta.find(iv => iv.descripcion === itemMatch.descripcion);
                  if (itemVenta) {
                    // Actualizar cantidad_ejecutada con la nueva cantidad_real
                    await itemPresupuestoVentaService.update(itemVenta.id, {
                      cantidad_ejecutada: nuevaCantidadReal
                    });
                  }
                }
              } catch (error) {
                console.error('Error al sincronizar cantidad_ejecutada:', error);
                // No detenemos el proceso si falla la sincronización
              }
            }
          }
        }
      }

      // Eliminar la orden de compra
      await ordenCompraService.delete(orden.id);
      toast.success(`Orden de compra ${orden.numero_oc} eliminada correctamente`);
      cargarOrdenesCompra();

      // Recargar presupuestos de costo si estamos en esa pestaña
      if (activeTab === 'presupuesto-costo') {
        cargarPresupuestosCosto();
      }

      // Recargar avance por gastar si estamos en esa pestaña
      if (activeTab === 'avance-por-gastar') {
        cargarAvancePorGastar();
      }
    } catch (error) {
      console.error('Error al eliminar orden de compra:', error);
      toast.error('Error al eliminar orden de compra');
    }
  };

  const cargarOrdenCompraParaVer = async (orden) => {
    try {
      // No usar setLoading(true) aquí para evitar que se recargue la vista
      // En su lugar, mostramos un toast de carga
      const loadingToast = toast.loading('Cargando orden de compra...');

      // 1. Obtener la OC completa con todos sus detalles
      const response = await ordenCompraService.getById(orden.id);
      const ordenCompleta = response.data;

      console.log('Orden completa:', ordenCompleta);

      // 2. Obtener el presupuesto de costo asociado
      let presupuestoCosto;

      if (ordenCompleta.presupuesto_costo) {
        // Si la OC tiene presupuesto_costo, obtenerlo directamente
        const presupuestoCostoResponse = await presupuestoCostoService.getById(ordenCompleta.presupuesto_costo);
        presupuestoCosto = presupuestoCostoResponse.data;
      } else {
        // Si no tiene presupuesto_costo, buscar el presupuesto activo del proyecto
        const proyectoId = ordenCompleta.proyecto || proyecto.id;
        const presupuestosCostoResponse = await presupuestoCostoService.getAll({
          proyecto: proyectoId,
          status: 'activo',
          limit: 1
        });

        const presupuestos = Array.isArray(presupuestosCostoResponse.data)
          ? presupuestosCostoResponse.data
          : (presupuestosCostoResponse.data.results || []);

        if (presupuestos.length === 0) {
          toast.dismiss(loadingToast);
          toast.error('No se encontró un presupuesto de costo activo para este proyecto');
          return;
        }

        presupuestoCosto = presupuestos[0];
      }

      console.log('Presupuesto de costo:', presupuestoCosto);

      // 3. Obtener todos los items del presupuesto de costo
      const itemsResponse = await itemPresupuestoCostoService.getAll({
        presupuesto_costo: presupuestoCosto.id,
        status: 'activo',
        limit: 1000
      });
      const items = Array.isArray(itemsResponse.data)
        ? itemsResponse.data
        : (itemsResponse.data.results || []);

      console.log('Items del presupuesto:', items);

      // 4. Mapear los detalles de la OC a los items expandidos
      const itemsConDetalles = items.map(item => {
        // Buscar si este item tiene un detalle en la OC
        const detalleOC = ordenCompleta.detalles?.find(d => d.descripcion === item.descripcion);

        if (detalleOC) {
          // Marcar este item como seleccionado y cargar sus datos de la OC
          return {
            ...item,
            selected: true,
            cantidadOC: detalleOC.cantidad,
            diasOC: detalleOC.cantidad_dias || 1,
            precioUnitarioOC: detalleOC.precio_unitario,
            detalleOCId: detalleOC.id
          };
        }
        return item;
      });

      console.log('Items con detalles:', itemsConDetalles);

      // 5. Configurar los items seleccionados (solo los que están en la OC)
      const idsItemsSeleccionados = itemsConDetalles
        .filter(item => item.selected)
        .map(item => item.id);

      console.log('IDs seleccionados:', idsItemsSeleccionados);

      // 6. Establecer los estados necesarios
      setPresupuestoSeleccionado(presupuestoCosto);
      setItemsExpandidos(itemsConDetalles);
      setItemsSeleccionados(idsItemsSeleccionados);
      setOrdenCompraEditando(ordenCompleta);

      // Cerrar el toast de carga
      toast.dismiss(loadingToast);
    } catch (error) {
      console.error('Error al cargar orden de compra:', error);
      toast.error('Error al cargar la orden de compra');
    }
  };

  const handleVerOrdenCompra = (orden) => {
    // Mostrar el modal de vista previa (read-only)
    setOrdenCompraParaVer(orden);
    setShowVerEditarOC(true);
  };

  const handleEditarOrdenCompra = (orden) => {
    // Mostrar el modal en modo edición
    setOrdenCompraParaVer(orden);
    setShowVerEditarOC(true);
  };

  // Funciones para Rendiciones
  const handleInputChangeRendicion = (e) => {
    const { name, value } = e.target;
    setFormDataRendicion(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAgregarDetalleRendicion = () => {
    setDetallesRendicion(prev => [...prev, {
      id: Date.now(),
      descripcion: '',
      monto: 0,
      categoria: '',
      notas: '',
      isNew: true
    }]);
  };

  const handleEliminarDetalleRendicion = async (detalle) => {
    if (detalle.isNew) {
      setDetallesRendicion(prev => prev.filter(d => d.id !== detalle.id));
    } else {
      try {
        await detalleRendicionService.delete(detalle.id);
        toast.success('Detalle eliminado');
        setDetallesRendicion(prev => prev.filter(d => d.id !== detalle.id));
      } catch (error) {
        console.error('Error al eliminar detalle:', error);
        toast.error('Error al eliminar detalle');
      }
    }
  };

  const handleChangeDetalleRendicion = (id, field, value) => {
    setDetallesRendicion(prev => prev.map(d =>
      d.id === id ? { ...d, [field]: value } : d
    ));
  };

  const resetFormRendicion = () => {
    setFormDataRendicion({
      orden_compra: '',
      proveedor: '',
      numero_comprobante: '',
      tipo_comprobante: 'factura',
      fecha_comprobante: new Date().toISOString().split('T')[0],
      descripcion: '',
      estado_aprobacion: 'pendiente_aprobacion',
    });
    setDetallesRendicion([]);
    setRendicionEditando(null);
  };

  const handleCrearRendicion = () => {
    resetFormRendicion();
    setShowCrearRendicion(true);
  };

  const handleEditarRendicion = async (rendicion) => {
    setRendicionEditando(rendicion);
    setFormDataRendicion({
      orden_compra: rendicion.orden_compra || '',
      proveedor: rendicion.proveedor || '',
      numero_comprobante: rendicion.numero_comprobante,
      tipo_comprobante: rendicion.tipo_comprobante,
      fecha_comprobante: rendicion.fecha_comprobante,
      descripcion: rendicion.descripcion || '',
      estado_aprobacion: rendicion.estado_aprobacion,
    });

    // Cargar detalles de la rendición
    try {
      const response = await rendicionService.getById(rendicion.id);
      const detalles = response.data.detalles || [];
      setDetallesRendicion(detalles);
    } catch (error) {
      console.error('Error al cargar detalles:', error);
    }

    setShowCrearRendicion(true);
  };

  const handleEliminarRendicion = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta rendición?')) {
      try {
        await rendicionService.delete(id);
        toast.success('Rendición eliminada');
        cargarRendiciones();
      } catch (error) {
        console.error('Error al eliminar rendición:', error);
        toast.error('Error al eliminar rendición');
      }
    }
  };

  const handleSubmitRendicion = async (e) => {
    e.preventDefault();

    if (detallesRendicion.length === 0) {
      toast.error('Debes agregar al menos un detalle');
      return;
    }

    try {
      const rendicionData = {
        proyecto: proyecto.id,
        ...formDataRendicion,
        status: 'activo',
      };

      let rendicionId;

      if (rendicionEditando) {
        await rendicionService.update(rendicionEditando.id, rendicionData);
        rendicionId = rendicionEditando.id;
        toast.success('Rendición actualizada');
      } else {
        const response = await rendicionService.create(rendicionData);
        rendicionId = response.data.id;
        toast.success('Rendición creada');
      }

      // Guardar detalles
      for (const detalle of detallesRendicion) {
        const detalleData = {
          rendicion: rendicionId,
          descripcion: detalle.descripcion,
          monto: parseFloat(detalle.monto),
          categoria: detalle.categoria || null,
          notas: detalle.notas || '',
          status: 'activo'
        };

        if (detalle.isNew) {
          await detalleRendicionService.create(detalleData);
        } else {
          await detalleRendicionService.update(detalle.id, detalleData);
        }
      }

      setShowCrearRendicion(false);
      resetFormRendicion();
      cargarRendiciones();
    } catch (error) {
      console.error('Error al guardar rendición:', error);
      toast.error('Error al guardar rendición');
    }
  };

  const handleAprobarRendicion = async (id) => {
    try {
      await rendicionService.aprobar(id);
      toast.success('Rendición aprobada');
      cargarRendiciones();
    } catch (error) {
      console.error('Error al aprobar rendición:', error);
      toast.error('Error al aprobar rendición');
    }
  };

  const handleRechazarRendicion = async (id) => {
    const motivo = prompt('Indica el motivo del rechazo:');
    if (motivo) {
      try {
        await rendicionService.rechazar(id, motivo);
        toast.success('Rendición rechazada');
        cargarRendiciones();
      } catch (error) {
        console.error('Error al rechazar rendición:', error);
        toast.error('Error al rechazar rendición');
      }
    }
  };

  const handleToggleRendicion = async (rendicion) => {
    if (rendicionExpandida === rendicion.id) {
      setRendicionExpandida(null);
    } else {
      setRendicionExpandida(rendicion.id);
      // Cargar detalles si no están cargados
      if (!rendicion.detalles) {
        try {
          const response = await rendicionService.getById(rendicion.id);
          const rendicionCompleta = response.data;
          setRendiciones(prev => prev.map(r =>
            r.id === rendicion.id ? rendicionCompleta : r
          ));
        } catch (error) {
          console.error('Error al cargar detalles:', error);
        }
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Parse date as YYYY-MM-DD to avoid timezone offset issues
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day).toLocaleDateString('es-CL');
  };

  const getEstadoLabel = (estado) => {
    const labels = {
      borrador: 'Borrador',
      enviado_aprobacion_interna: 'Enviado Aprobación Interna',
      aprobacion_interna: 'Aprobación Interna',
      enviado_aprobacion_cliente: 'Enviado Aprobación Cliente',
      aprobacion_cliente: 'Aprobación Cliente',
      presupuesto_costo_creado: 'Presupuesto Costo Creado',
      terminado: 'Terminado',
      rechazado: 'Rechazado',
      pendiente: 'Pendiente',
      aprobado: 'Aprobado',
      emitida: 'Emitida',
      recibida: 'Recibida',
      facturada: 'Facturada',
      cancelada: 'Cancelada',
    };
    return labels[estado] || estado;
  };

  const calcularTotalItems = (items) => {
    return items.reduce((total, item) => total + (parseFloat(item.subtotal) || 0), 0);
  };

  const calcularMarkupPromedio = (items) => {
    const itemsConMarkup = items.filter(item => item.markup && item.markup > 0);
    if (itemsConMarkup.length === 0) return 0;
    const sumaMarkup = itemsConMarkup.reduce((total, item) => total + parseFloat(item.markup), 0);
    return sumaMarkup / itemsConMarkup.length;
  };

  const renderItemsVenta = (items) => {
    const subtotal = calcularTotalItems(items);
    const markupPromedio = calcularMarkupPromedio(items);
    const comisionAgenciaPorcentaje = presupuestoSeleccionado?.comision_agencia_porcentaje || 0;
    const comisionAgenciaMonto = (subtotal * comisionAgenciaPorcentaje) / 100;
    const descuentoPorcentaje = presupuestoSeleccionado?.descuento_porcentaje || 0;
    const descuentoTotal = (subtotal * descuentoPorcentaje) / 100;
    const subtotalConAjustes = subtotal + comisionAgenciaMonto - descuentoTotal;
    const ivaPorcentaje = presupuestoSeleccionado?.iva_porcentaje || 19;
    const aplicarIva = presupuestoSeleccionado?.aplicar_iva !== false;
    const ivaMonto = aplicarIva ? (subtotalConAjustes * ivaPorcentaje) / 100 : 0;
    const total = subtotalConAjustes + ivaMonto;

    // Determinar si se pueden editar items - Usar los mismos estados bloqueados que en presupuesto de venta
    const estadosBloqueo = ['aprobacion_interna', 'enviado_aprobacion_cliente', 'aprobacion_cliente', 'terminado', 'en_ejecucion'];
    const puedoEditarItems = !estadosBloqueo.includes(presupuestoSeleccionado?.estado);

    return (
      <div className="items-expanded">
        <div className="items-expanded-header">
          <h4>Items del Presupuesto ({items.length})</h4>
        </div>
        <div className="items-table-container">
          <table className="items-table">
            <thead>
              <tr>
                <th>Descripción</th>
                <th>Cantidad</th>
                <th>Días</th>
                <th>Unidad</th>
                <th>Costo Unit.</th>
                <th>Markup %</th>
                <th>Precio</th>
                <th>Subtotal</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.descripcion}</td>
                  <td>{item.cantidad}</td>
                  <td>{item.cantidad_dias || '-'}</td>
                  <td>{item.unidad}</td>
                  <td>{item.costo_unitario ? formatCurrency(item.costo_unitario) : '-'}</td>
                  <td>{item.markup ? `${item.markup}%` : '-'}</td>
                  <td>{formatCurrency(item.precio_unitario)}</td>
                  <td>{formatCurrency(item.subtotal)}</td>
                  <td className="acciones">
                    <button
                      className="btn-icon"
                      disabled={!puedoEditarItems}
                      onClick={() => handleEditarItemVenta(item)}
                      title={puedoEditarItems ? "Editar item" : "No se puede editar en este estado"}
                      style={!puedoEditarItems ? { opacity: '0.5', cursor: 'not-allowed' } : {}}
                    >
                      <FiEdit />
                    </button>
                    <button
                      className="btn-icon danger"
                      disabled={!puedoEditarItems}
                      onClick={() => handleEliminarItemVenta(item.id)}
                      title={puedoEditarItems ? "Eliminar item" : "No se puede eliminar en este estado"}
                      style={!puedoEditarItems ? { opacity: '0.5', cursor: 'not-allowed' } : {}}
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totales con estilo de OC */}
          <div className="monto-desglose" style={{ marginTop: '1rem' }}>
            <div className="monto-row subtotal">
              <span>Subtotal:</span>
              <span className="monto-value">{formatCurrency(subtotal)}</span>
            </div>

            <div className="monto-row">
              <span>Markup Promedio:</span>
              <span className="monto-value">{markupPromedio.toFixed(2)}%</span>
            </div>

            <div className="monto-row">
              <span>Comisión Agencia ({Number(comisionAgenciaPorcentaje).toFixed(2)}%):</span>
              <span className="monto-value" style={{ color: '#059669' }}>
                +{formatCurrency(comisionAgenciaMonto)}
              </span>
            </div>

            <div className="monto-row">
              <span>Descuento ({Number(descuentoPorcentaje).toFixed(2)}%):</span>
              <span className="monto-value" style={{ color: '#dc2626' }}>
                -{formatCurrency(descuentoTotal)}
              </span>
            </div>

            {aplicarIva && (
              <div className="monto-row iva">
                <span>IVA ({Number(ivaPorcentaje).toFixed(2)}%):</span>
                <span className="monto-value">+{formatCurrency(ivaMonto)}</span>
              </div>
            )}

            <div className="monto-row total">
              <span>Total:</span>
              <span className="monto-total-value">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderItemsCosto = (items) => {
    // Calcular subtotal de cada item: Cantidad Real * Precio Unit. Real * Días Real
    const itemsConSubtotales = items.map(item => {
      const cantidadReal = parseFloat(item.cantidad_real) || 0;
      const diasReal = parseFloat(item.dias_real) || 0;
      const precioUnitarioReal = parseFloat(item.precio_unitario_real) || 0;
      const subtotalItem = cantidadReal * diasReal * precioUnitarioReal;

      const markupItem = item.costo_unitario && item.costo_unitario > 0 && precioUnitarioReal > 0
        ? ((item.costo_unitario - precioUnitarioReal) / item.costo_unitario) * 100
        : 0;
      return { ...item, subtotalCalculado: subtotalItem, markupCalculado: markupItem };
    });

    const subtotal = itemsConSubtotales.reduce((total, item) => total + item.subtotalCalculado, 0);
    const markupPromedio = calcularMarkupPromedio(items);
    const comisionAgenciaPorcentaje = presupuestoSeleccionado?.comision_agencia_porcentaje || 0;
    const comisionAgenciaMonto = (subtotal * comisionAgenciaPorcentaje) / 100;
    const descuentoPorcentaje = presupuestoSeleccionado?.descuento_porcentaje || 0;
    const descuentoTotal = (subtotal * descuentoPorcentaje) / 100;
    const subtotalConAjustes = subtotal + comisionAgenciaMonto - descuentoTotal;
    const ivaPorcentaje = presupuestoSeleccionado?.iva_porcentaje || 19;
    const aplicarIva = presupuestoSeleccionado?.aplicar_iva !== false;
    const ivaMonto = aplicarIva ? (subtotalConAjustes * ivaPorcentaje) / 100 : 0;
    const total = subtotalConAjustes + ivaMonto;

    return (
      <div className="items-expanded">
        <div className="items-expanded-header">
          <h4>Items del Presupuesto ({items.length})</h4>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className={`btn-primary btn-sm ${modoSeleccion ? 'active' : ''}`}
              onClick={handleToggleModoSeleccion}
            >
              {modoSeleccion ? 'Cancelar Selección' : 'Seleccionar items'}
            </button>
            {modoSeleccion && itemsSeleccionados.length > 0 && (
              <button
                className="btn-primary btn-sm"
                onClick={handleSolicitarOC}
              >
                Solicitar OC ({itemsSeleccionados.length} items)
              </button>
            )}
          </div>
        </div>
        <div className="items-table-container">
          <table className="items-table">
            <thead>
              <tr>
                {modoSeleccion && <th style={{ width: '50px' }}>Sel.</th>}
                <th>Descripción</th>
                <th>Unidad</th>
                <th>Días</th>
                <th>Cantidad</th>
                <th>Costo Unit. Esperado</th>
                <th>Cant. Real</th>
                <th>Días Real</th>
                <th>Precio Unit. Real</th>
                <th>Markup %</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {itemsConSubtotales.map((item) => (
                <tr key={item.id}>
                  {modoSeleccion && (
                    <td>
                      <input
                        type="checkbox"
                        checked={itemsSeleccionados.includes(item.id)}
                        onChange={() => handleToggleItemSeleccion(item.id)}
                      />
                    </td>
                  )}
                  <td>{item.descripcion}</td>
                  <td>{item.unidad}</td>
                  <td>{item.cantidad_dias || '-'}</td>
                  <td>{item.cantidad}</td>
                  <td>{item.costo_unitario ? formatCurrency(item.costo_unitario) : '-'}</td>
                  <td>{item.cantidad_real || '-'}</td>
                  <td>{item.dias_real || '-'}</td>
                  <td>{item.precio_unitario_real ? formatCurrency(item.precio_unitario_real) : '-'}</td>
                  <td>{item.markupCalculado ? `${item.markupCalculado.toFixed(2)}%` : '-'}</td>
                  <td>{formatCurrency(item.subtotalCalculado)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totales con estilo de OC */}
          <div className="monto-desglose" style={{ marginTop: '1rem' }}>
            <div className="monto-row subtotal">
              <span>Subtotal:</span>
              <span className="monto-value">{formatCurrency(subtotal)}</span>
            </div>

            <div className="monto-row">
              <span>Markup Promedio:</span>
              <span className="monto-value">{markupPromedio.toFixed(2)}%</span>
            </div>

            {comisionAgenciaPorcentaje > 0 && (
              <div className="monto-row">
                <span>Comisión Agencia ({Number(comisionAgenciaPorcentaje).toFixed(2)}%):</span>
                <span className="monto-value" style={{ color: '#059669' }}>
                  +{formatCurrency(comisionAgenciaMonto)}
                </span>
              </div>
            )}

            {descuentoPorcentaje > 0 && (
              <div className="monto-row">
                <span>Descuento ({Number(descuentoPorcentaje).toFixed(2)}%):</span>
                <span className="monto-value" style={{ color: '#dc2626' }}>
                  -{formatCurrency(descuentoTotal)}
                </span>
              </div>
            )}

            {aplicarIva && (
              <div className="monto-row iva">
                <span>IVA ({Number(ivaPorcentaje).toFixed(2)}%):</span>
                <span className="monto-value">+{formatCurrency(ivaMonto)}</span>
              </div>
            )}

            <div className="monto-row total">
              <span>Total:</span>
              <span className="monto-total-value">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Funciones para editar Información General
  const handleToggleEditarInformacionGeneral = () => {
    if (!editarInformacionGeneral) {
      // Inicializar el formulario con los datos actuales del proyecto
      setFormDataProyecto({
        nombre: proyecto.nombre || '',
        tipo_proyecto: proyecto.tipo_proyecto || '',
        cliente: proyecto.cliente || '',
        linea_negocio: proyecto.linea_negocio || '',
        fecha_cotizacion: proyecto.fecha_cotizacion ? proyecto.fecha_cotizacion.split('T')[0] : '',
        margen_objetivo: proyecto.margen_objetivo || '',
        fecha_inicio_planeado: proyecto.fecha_inicio_planeado ? proyecto.fecha_inicio_planeado.split('T')[0] : '',
        fecha_termino_planeada: proyecto.fecha_termino_planeada ? proyecto.fecha_termino_planeada.split('T')[0] : ''
      });

      // Cargar clientes y líneas de negocio
      if (clientes.length === 0) {
        cargarClientes();
      }
      if (lineasNegocio.length === 0) {
        cargarLineasNegocio();
      }
    }
    setEditarInformacionGeneral(!editarInformacionGeneral);
  };

  const handleChangeProyectoField = (field, value) => {
    setFormDataProyecto(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGuardarInformacionGeneral = async () => {
    try {
      setLoading(true);
      await proyectoService.update(proyecto.id, formDataProyecto);

      // Actualizar el proyecto localmente
      const proyectoActualizado = {
        ...proyectoLocal,
        ...formDataProyecto
      };

      // Actualizar estado local para reflejar cambios inmediatamente
      setProyectoLocal(proyectoActualizado);

      // Notificar al componente padre
      if (onUpdate) {
        onUpdate(proyectoActualizado);
      }

      setEditarInformacionGeneral(false);
      toast.success('Información del proyecto actualizada exitosamente');
    } catch (error) {
      console.error('Error al guardar proyecto:', error);
      toast.error('Error al guardar los cambios');
    } finally {
      setLoading(false);
    }
  };

  const renderGeneral = () => {
    // Calcular total presupuestado desde las áreas asignadas a los presupuestos de venta
    const totalPresupuestado = presupuestosVenta.reduce((total, p) => total + (parseFloat(p.subtotal_venta) || 0), 0);
    // Calcular costo esperado desde los items de los presupuestos de venta
    const costoEsperado = presupuestosVenta.reduce((total, p) => total + (parseFloat(p.subtotal_costo) || 0), 0);

    // Calcular costo real total (Subtotal Presupuesto Costos + Workflow Kanban + Workflow Proyectos)
    const costoRealTotal = calcularTotalGastado();

    // Calcular utilidades y márgenes
    const utilidadEsperada = totalPresupuestado - costoEsperado;
    const utilidadReal = totalPresupuestado - costoRealTotal;
    const margenEsperado = totalPresupuestado > 0 ? ((totalPresupuestado - costoEsperado) / totalPresupuestado) * 100 : 0;
    // Margen Real debe ser 0% cuando Presupuestado es 0 o Costo Real es 0
    const margenReal = totalPresupuestado === 0 ? 0 : ((totalPresupuestado - costoRealTotal) / totalPresupuestado) * 100;

    return (
      <div className="tab-content-general">
        <div className="section-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: '2px solid #e5e7eb'
        }}>
          <h3 style={{ margin: 0 }}>📄 Información General</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleToggleEditarInformacionGeneral}
              className="btn-edit-info"
              style={{
                padding: '0.5rem 1rem',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                transition: 'background-color 0.2s'
              }}
            >
              {editarInformacionGeneral ? '❌ Cancelar' : '✏️ Editar Información'}
            </button>
            <button
              onClick={() => {
                if (window.confirm('¿Estás seguro de que deseas eliminar este proyecto?')) {
                  // Implementar eliminación de proyecto
                  console.log('Eliminando proyecto');
                }
              }}
              className="btn-delete-post"
              style={{
                padding: '0.5rem 1rem',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                transition: 'background-color 0.2s'
              }}
            >
              🗑️ Eliminar Proyecto
            </button>
          </div>
        </div>

        <div className="general-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>ℹ️ Información General</h3>
            <span
              className="estado-badge"
              style={{
                backgroundColor: estadoColors[proyectoLocal.estado] || '#6b7280',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'white'
              }}
            >
              {estadoLabels[proyectoLocal.estado] || proyectoLocal.estado}
            </span>
          </div>
          {editarInformacionGeneral ? (
            <div className="info-grid">
              <div className="info-item">
                <label>Proyecto:</label>
                <input
                  type="text"
                  value={formDataProyecto.nombre}
                  onChange={(e) => handleChangeProyectoField('nombre', e.target.value)}
                  placeholder="Nombre del proyecto"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div className="info-item">
                <label>Tipo de Proyecto:</label>
                <select
                  value={formDataProyecto.tipo_proyecto}
                  onChange={(e) => handleChangeProyectoField('tipo_proyecto', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Seleccionar tipo...</option>
                  <option value="redes_sociales">Redes Sociales</option>
                  <option value="evento">Evento</option>
                  <option value="proyecto">Proyecto</option>
                </select>
              </div>
              <div className="info-item">
                <label>Cliente:</label>
                <select
                  value={formDataProyecto.cliente}
                  onChange={(e) => handleChangeProyectoField('cliente', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Seleccionar cliente...</option>
                  {clientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombre || cliente.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="info-item">
                <label>Línea de Negocio:</label>
                <select
                  value={formDataProyecto.linea_negocio}
                  onChange={(e) => handleChangeProyectoField('linea_negocio', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Seleccionar línea de negocio...</option>
                  {lineasNegocio.map((linea) => (
                    <option key={linea.id} value={linea.id}>
                      {linea.nombre || linea.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="info-item">
                <label>Fecha Cotización:</label>
                <input
                  type="date"
                  value={formDataProyecto.fecha_cotizacion}
                  onChange={(e) => handleChangeProyectoField('fecha_cotizacion', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div className="info-item">
                <label>Margen Objetivo (%):</label>
                <input
                  type="number"
                  value={formDataProyecto.margen_objetivo}
                  onChange={(e) => handleChangeProyectoField('margen_objetivo', e.target.value)}
                  placeholder="0"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div className="info-item">
                <label>Fecha Inicio Planeado:</label>
                <input
                  type="date"
                  value={formDataProyecto.fecha_inicio_planeado}
                  onChange={(e) => handleChangeProyectoField('fecha_inicio_planeado', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div className="info-item">
                <label>Fecha Término Planeada:</label>
                <input
                  type="date"
                  value={formDataProyecto.fecha_termino_planeada}
                  onChange={(e) => handleChangeProyectoField('fecha_termino_planeada', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="info-grid">
              <div className="info-item">
                <label>Proyecto:</label>
                <span>{proyectoLocal.nombre}</span>
              </div>
              <div className="info-item">
                <label>Tipo de Proyecto:</label>
                <span>
                  {proyectoLocal.tipo_proyecto === 'redes_sociales' && 'Redes Sociales'}
                  {proyectoLocal.tipo_proyecto === 'evento' && 'Evento'}
                  {proyectoLocal.tipo_proyecto === 'proyecto' && 'Proyecto'}
                  {!['redes_sociales', 'evento', 'proyecto'].includes(proyectoLocal.tipo_proyecto) && proyectoLocal.tipo_proyecto}
                </span>
              </div>
              <div className="info-item">
                <label>Cliente:</label>
                <span>{proyectoLocal.cliente_nombre}</span>
              </div>
              <div className="info-item">
                <label>Línea de Negocio:</label>
                <span>{proyectoLocal.linea_negocio_nombre || '-'}</span>
              </div>
              <div className="info-item">
                <label>Fecha Cotización:</label>
                <span>{formatDate(proyectoLocal.fecha_cotizacion)}</span>
              </div>
              <div className="info-item">
                <label>Margen Objetivo:</label>
                <span>{proyectoLocal.margen_objetivo}%</span>
              </div>
              <div className="info-item">
                <label>Fecha Inicio Planeado:</label>
                <span>{formatDate(proyectoLocal.fecha_inicio_planeado)}</span>
              </div>
              <div className="info-item">
                <label>Fecha Término Planeada:</label>
                <span>{formatDate(proyectoLocal.fecha_termino_planeada)}</span>
              </div>
            </div>
          )}

          {editarInformacionGeneral && (
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end',
              marginTop: '1.5rem',
              paddingTop: '1.5rem',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={() => setEditarInformacionGeneral(false)}
                className="btn-secondary"
                style={{
                  padding: '0.5rem 1.5rem',
                  background: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  transition: 'background-color 0.2s'
                }}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={handleGuardarInformacionGeneral}
                className="btn-primary"
                style={{
                  padding: '0.5rem 1.5rem',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  transition: 'background-color 0.2s'
                }}
                disabled={loading}
              >
                {loading ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          )}
        </div>

        {proyectoLocal.descripcion && (
          <div className="detail-section">
            <h3>Descripción</h3>
            <p>{proyectoLocal.descripcion}</p>
          </div>
        )}

        <AuditHistory
          createdBy={proyectoLocal.created_by}
          createdAt={proyectoLocal.fecha_creacion}
          editedBy={proyectoLocal.edited_by}
          editedAt={proyectoLocal.fecha_edicion}
        />
      </div>
    );
  };

  const renderPresupuestoVenta = () => (
    <div className="tab-content-presupuestos">
      <div className="section-header">
        <h3 style={{ color: '#fff' }}>Presupuestos de Venta</h3>
        <button
          className="btn-primary btn-sm"
          onClick={() => setShowCrearPresupuestoVenta(true)}
        >
          <FiPlus /> Nuevo Presupuesto
        </button>
      </div>

      {loading ? (
        <Loading fullScreen={false} size="sm" mensaje="Cargando presupuestos" />
      ) : presupuestosVenta.length > 0 ? (
        <div className="presupuestos-list">
          {presupuestosVenta.map((presupuesto) => {
            // Calcular valores para este presupuesto
            const totalPresupuestado = parseFloat(presupuesto.subtotal_venta) || 0;
            const costoEsperado = parseFloat(presupuesto.subtotal_costo) || 0;
            const utilidadEsperada = totalPresupuestado - costoEsperado;
            const margenEsperado = totalPresupuestado > 0
              ? ((utilidadEsperada / totalPresupuestado) * 100).toFixed(2)
              : '0.00';

            return (
              <div
                key={presupuesto.id}
                className="presupuesto-card-modern"
              >
                {/* Header de la card */}
                <div
                  className="presupuesto-card-header"
                  onClick={() => handleVerItems(presupuesto, 'venta')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="presupuesto-card-title">
                    {presupuesto.nombre && (
                      <span className="presupuesto-nombre" style={{ fontWeight: '600', marginRight: '10px', color: '#fff' }}>
                        {presupuesto.nombre}
                      </span>
                    )}
                    <span
                      className="estado-badge"
                      style={{ backgroundColor: estadoColors[presupuesto.estado] }}
                    >
                      {getEstadoLabel(presupuesto.estado)}
                    </span>
                  </div>
                  <div
                    className="presupuesto-card-acciones"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Botones de workflow según estado */}
                    {presupuesto.estado === 'borrador' && (
                      <button
                        className="btn-icon success"
                        onClick={() => handleEnviarAprobacion(presupuesto)}
                        title="Enviar a aprobación interna"
                      >
                        <FiSend />
                      </button>
                    )}
                    {presupuesto.estado === 'enviado_aprobacion_interna' && (
                      <>
                        <button
                          className="btn-icon success"
                          onClick={() => handleAprobar(presupuesto)}
                          title="Aprobar"
                        >
                          <FiCheck />
                        </button>
                        <button
                          className="btn-icon danger"
                          onClick={() => handleRechazar(presupuesto)}
                          title="Rechazar"
                        >
                          <FiX />
                        </button>
                      </>
                    )}
                    {presupuesto.estado === 'aprobacion_interna' && (
                      <>
                        <button
                          className="btn-icon success"
                          onClick={() => handleEnviarAprobacionCliente(presupuesto)}
                          title="Enviar a aprobación del cliente"
                        >
                          <FiSend />
                        </button>
                        <button
                          className="btn-icon danger"
                          onClick={() => handleRechazar(presupuesto)}
                          title="Rechazar internamente"
                        >
                          <FiX />
                        </button>
                      </>
                    )}
                    {presupuesto.estado === 'rechazo_interno' && (
                      <button
                        className="btn-icon success"
                        onClick={() => handleReintentarAprobacionInterna(presupuesto)}
                        title="Enviar a aprobación interna"
                      >
                        <FiSend />
                      </button>
                    )}
                    {presupuesto.estado === 'enviado_aprobacion_cliente' && (
                      <>
                        <button
                          className="btn-icon success"
                          onClick={() => handleAprobarCliente(presupuesto)}
                          title="Aprobación cliente"
                        >
                          <FiCheck />
                        </button>
                        <button
                          className="btn-icon danger"
                          onClick={() => handleRechazarCliente(presupuesto)}
                          title="Rechazo cliente"
                        >
                          <FiX />
                        </button>
                      </>
                    )}
                    {presupuesto.estado === 'rechazo_cliente' && (
                      <button
                        className="btn-icon success"
                        onClick={() => handleReintentarAprobacionInterna(presupuesto)}
                        title="Enviar a aprobación interna"
                      >
                        <FiSend />
                      </button>
                    )}
                    {presupuesto.estado === 'aprobacion_cliente' && (
                      <button
                        className="btn-sm btn-success"
                        onClick={() => handleAbrirSeleccionItems(presupuesto)}
                        title="Asignar items a presupuesto de costos"
                        style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      >
                        Asignar presupuesto
                      </button>
                    )}

                    {/* Estados que bloquean edición */}
                    {(() => {
                      const estadosBloqueados = ['aprobacion_interna', 'enviado_aprobacion_cliente', 'aprobacion_cliente', 'terminado', 'en_ejecucion'];
                      const estaBloqueado = estadosBloqueados.includes(presupuesto.estado);

                      return (
                        <>
                          {/* Botones comunes */}
                          <button
                            className="btn-icon"
                            onClick={() => handleEditarPresupuestoVenta(presupuesto)}
                            title="Editar presupuesto"
                          >
                            <FiEdit />
                          </button>
                          <button
                            className="btn-icon"
                            onClick={() => handleDuplicarPresupuestoVenta(presupuesto)}
                            title="Duplicar presupuesto"
                          >
                            <FiCopy />
                          </button>
                          <button
                            className={`btn-icon ${presupuestoExpandidoId === presupuesto.id ? 'active' : ''}`}
                            onClick={() => handleVerItems(presupuesto, 'venta')}
                            title={presupuestoExpandidoId === presupuesto.id ? "Ocultar items" : "Ver items"}
                          >
                            <FiEye />
                          </button>
                          <button
                            className="btn-icon danger"
                            disabled={estaBloqueado}
                            onClick={() => handleEliminarPresupuestoVenta(presupuesto)}
                            title={estaBloqueado ? "No se puede eliminar en este estado" : "Eliminar presupuesto"}
                            style={estaBloqueado ? { opacity: '0.5', cursor: 'not-allowed' } : {}}
                          >
                            <FiTrash2 />
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Grid de métricas financieras */}
                <div className="presupuesto-metricas-grid">
                  <div className="metrica-card">
                    <div className="metrica-icon">💰</div>
                    <div className="metrica-info">
                      <div className="metrica-label">Total Presupuestado</div>
                      <div className="metrica-valor">{formatCurrency(totalPresupuestado)}</div>
                    </div>
                  </div>

                  <div className="metrica-card">
                    <div className="metrica-icon">💳</div>
                    <div className="metrica-info">
                      <div className="metrica-label">Costo Esperado</div>
                      <div className="metrica-valor gastado">{formatCurrency(costoEsperado)}</div>
                    </div>
                  </div>

                  <div className="metrica-card">
                    <div className="metrica-icon">📈</div>
                    <div className="metrica-info">
                      <div className="metrica-label">Utilidad Esperada</div>
                      <div className={`metrica-valor ${utilidadEsperada >= 0 ? 'positivo' : 'negativo'}`}>
                        {formatCurrency(utilidadEsperada)}
                      </div>
                    </div>
                  </div>

                  <div className="metrica-card highlight">
                    <div className="metrica-icon">📊</div>
                    <div className="metrica-info">
                      <div className="metrica-label">Margen Esperado</div>
                      <div className={`metrica-valor ${utilidadEsperada >= 0 ? 'positivo' : 'negativo'}`}>
                        {margenEsperado}%
                      </div>
                      <div className="metrica-sublabel">
                        <span className={utilidadEsperada >= 0 ? 'positivo' : 'negativo'}>
                          {utilidadEsperada >= 0 ? 'Ganancia proyectada' : 'Pérdida proyectada'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items expandidos */}
                {presupuestoExpandidoId === presupuesto.id && (
                  <div className="items-section-expanded">
                    <div className="hierarchy-section">
                      <div className="hierarchy-header">
                        <h4>Estructura Jerárquica</h4>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn-primary"
                            onClick={() => {
                              setPresupuestoParaAsignarCatalogo(presupuesto);
                              setTipoPresupuestoParaAsignarCatalogo('venta');
                              setAsignarDesdeCatalogoModalOpen(true);
                            }}
                          >
                            <FiPlus /> Agregar Catálogo
                          </button>
                          <button
                            className="btn-primary"
                            onClick={() => {
                              setPresupuestoParaAgregarArea(presupuesto);
                              setTipoPresupuestoParaAgregarArea('venta');
                              setAgregarAreaModalOpen(true);
                            }}
                          >
                            <FiPlus /> Agregar Área
                          </button>
                        </div>
                      </div>
                      <PresupuestoVentaHierarchy
                        presupuestoVentaId={presupuesto.id}
                        cuentaId={proyecto.cuenta}
                        onUpdate={cargarPresupuestosVenta}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <FiFileText size={48} />
          <p>No hay presupuestos de venta para este proyecto</p>
        </div>
      )}
    </div>
  );

  const renderPresupuestoCosto = () => {
    // Usar total gastado calculado desde OCs
    const costoRealTotal = totalGastadoProyecto;

    return (
      <div className="tab-content-presupuestos">
        <div className="section-header">
          <h3 style={{ color: '#fff' }}>Presupuestos de Costo</h3>
          <button
            className="btn-primary btn-sm"
            onClick={() => setShowCrearPresupuestoCosto(true)}
          >
            <FiPlus /> Nuevo Presupuesto
          </button>
        </div>

        {loading ? (
          <Loading fullScreen={false} size="sm" mensaje="Cargando presupuestos" />
        ) : presupuestosCosto.length > 0 ? (
          <div className="presupuestos-list">
            {presupuestosCosto.map((presupuesto) => {
              // Obtener totales desde los estados
              const totalEjecutado = totalEjecutadoPresupuestos[presupuesto.id] || 0;
              const totalGastadoGeneral = calcularTotalGastado();
              const utilidadActual = totalEjecutado - totalGastadoGeneral;
              const utilidadRealPorcentaje = totalEjecutado > 0
                ? ((utilidadActual / totalEjecutado) * 100).toFixed(2)
                : '0.00';

              return (
                <div
                  key={presupuesto.id}
                  className="presupuesto-card-modern"
                >
                  {/* Header de la card */}
                  <div
                    className="presupuesto-card-header"
                    onClick={() => handleVerItems(presupuesto, 'costo')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="presupuesto-card-title">
                      {presupuesto.nombre && (
                        <span className="presupuesto-nombre" style={{ fontWeight: '600', marginRight: '10px', color: '#fff' }}>
                          {presupuesto.nombre}
                        </span>
                      )}
                      <span
                        className="estado-badge"
                        style={{ backgroundColor: estadoColors[presupuesto.estado] }}
                      >
                        {getEstadoLabel(presupuesto.estado)}
                      </span>
                    </div>
                    <div className="presupuesto-card-acciones">
                      <button
                        className="btn-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditarPresupuestoCosto(presupuesto);
                        }}
                        title="Editar presupuesto"
                      >
                        <FiEdit />
                      </button>
                      <button
                        className={`btn-icon ${presupuestoExpandidoId === presupuesto.id ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVerItems(presupuesto, 'costo');
                        }}
                        title={presupuestoExpandidoId === presupuesto.id ? "Ocultar items" : "Ver items"}
                      >
                        <FiEye />
                      </button>
                    </div>
                  </div>

                  {/* Grid de métricas financieras */}
                  <div className="presupuesto-metricas-grid">
                    <div className="metrica-card">
                      <div className="metrica-icon">📋</div>
                      <div className="metrica-info">
                        <div className="metrica-label">Costo Presupuestado</div>
                        <div className="metrica-valor">{formatCurrency(parseFloat(presupuestosVenta[0]?.subtotal_costo) || 0)}</div>
                      </div>
                    </div>

                    <div className="metrica-card">
                      <div className="metrica-icon">💼</div>
                      <div className="metrica-info">
                        <div className="metrica-label">Costo Esperado</div>
                        <div className="metrica-valor">{formatCurrency(presupuesto.total_presupuestado || presupuesto.total || 0)}</div>
                      </div>
                    </div>

                    <div className="metrica-card">
                      <div className="metrica-icon">✅</div>
                      <div className="metrica-info">
                        <div className="metrica-label">Total Ejecutado</div>
                        <div className="metrica-valor ejecutado">{formatCurrency(totalEjecutado)}</div>
                      </div>
                    </div>

                    <div className="metrica-card">
                      <div className="metrica-icon">💸</div>
                      <div className="metrica-info">
                        <div className="metrica-label">Total Gastado</div>
                        <div className="metrica-valor gastado">{formatCurrency(calcularTotalGastado())}</div>
                        <div className="metrica-sublabel">
                          Presupuesto + Workflows
                        </div>
                      </div>
                    </div>

                    <div className="metrica-card highlight">
                      <div className="metrica-icon">💰</div>
                      <div className="metrica-info">
                        <div className="metrica-label">DESVIACIÓN</div>
                        <div className={`metrica-valor ${utilidadActual >= 0 ? 'positivo' : 'negativo'}`}>
                          {formatCurrency(utilidadActual)}
                        </div>
                        <div className="metrica-sublabel">
                          <span className={utilidadActual >= 0 ? 'positivo' : 'negativo'}>
                            {utilidadRealPorcentaje}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Items expandidos */}
                  {presupuestoExpandidoId === presupuesto.id && (
                    <div className="items-section-expanded">
                      <div className="hierarchy-section">
                        <div className="hierarchy-header" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginBottom: '16px' }}>
                          <button
                            className="btn-primary"
                            onClick={() => {
                              setPresupuestoParaAsignarCatalogo(presupuesto);
                              setTipoPresupuestoParaAsignarCatalogo('costo');
                              setAsignarDesdeCatalogoModalOpen(true);
                            }}
                          >
                            <FiPlus /> Agregar Catálogo
                          </button>
                          <button
                            className="btn-primary"
                            onClick={() => {
                              setPresupuestoParaAgregarArea(presupuesto);
                              setTipoPresupuestoParaAgregarArea('costo');
                              setAgregarAreaModalOpen(true);
                            }}
                          >
                            <FiPlus /> Agregar Área
                          </button>
                        </div>
                        <PresupuestoCostoHierarchy
                          presupuestoCostoId={presupuesto.id}
                          cuentaId={proyecto.cuenta}
                          onUpdate={cargarPresupuestosCosto}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <FiFileText size={48} />
            <p>No hay presupuestos de costo para este proyecto</p>
          </div>
        )}
      </div>
    );
  };

  const renderRecursosSolicitados = () => {
    return (
      <div className="tab-content-ordenes">
        <div className="section-header">
          <h3 style={{ color: '#fff' }}>Recursos Solicitados del Proyecto</h3>
        </div>

        {loadingRecursos ? (
          <Loading fullScreen={false} size="sm" mensaje="Cargando recursos" />
        ) : recursosProyecto.length > 0 ? (
          <div className="ordenes-table" style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ color: '#fff' }}>Presupuesto</th>
                  <th style={{ color: '#fff' }}>Área</th>
                  <th style={{ color: '#fff' }}>Partida</th>
                  <th style={{ color: '#fff' }}>Recurso</th>
                  <th style={{ color: '#fff', textAlign: 'right', fontWeight: 'bold' }}>Cant. Presupuesto</th>
                  <th style={{ color: '#fff', textAlign: 'right', fontWeight: 'bold' }}>Cant. Solicitada</th>
                  <th style={{ color: '#fff', textAlign: 'center' }}>% Avance</th>
                  <th style={{ color: '#fff', textAlign: 'right' }}>Precio Unit. Costo</th>
                  <th style={{ color: '#fff', textAlign: 'right' }}>Precio Unit. Venta</th>
                  <th style={{ color: '#fff', textAlign: 'right' }}>Markup %</th>
                  <th style={{ color: '#fff', textAlign: 'right' }}>Subtotal Costo</th>
                  <th style={{ color: '#fff', textAlign: 'right' }}>Subtotal</th>
                  <th style={{ color: '#fff', textAlign: 'right' }}>Total Ejecutado</th>
                  <th style={{ color: '#fff', textAlign: 'right' }}>Total Gastado</th>
                  <th style={{ color: '#fff' }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {recursosProyecto.map((recurso) => (
                  <tr key={recurso.id}>
                    <td>{recurso.presupuesto_nombre || '-'}</td>
                    <td>{recurso.area_nombre || '-'}</td>
                    <td>{recurso.partida_nombre || '-'}</td>
                    <td><strong>{recurso.nombre}</strong></td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{parseFloat(recurso.cantidad_presupuesto || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{parseFloat(recurso.cantidad_solicitada || 0).toFixed(2)}</td>
                    <td style={{ padding: '8px', minWidth: '150px' }}>
                      {(() => {
                        const cantidadPresupuesto = parseFloat(recurso.cantidad_presupuesto || 0);
                        const cantidadSolicitada = parseFloat(recurso.cantidad_solicitada || 0);
                        const porcentaje = cantidadPresupuesto > 0 ? (cantidadSolicitada / cantidadPresupuesto) * 100 : 0;

                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                              flex: 1,
                              height: '20px',
                              backgroundColor: '#e0e0e0',
                              borderRadius: '10px',
                              overflow: 'hidden',
                              position: 'relative'
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${Math.min(porcentaje, 100)}%`,
                                backgroundColor: porcentaje > 80 ? '#d9534f' : porcentaje > 50 ? '#f0ad4e' : '#5cb85c',
                                transition: 'width 0.3s ease',
                                borderRadius: '10px'
                              }} />
                            </div>
                            <span style={{
                              minWidth: '45px',
                              textAlign: 'right',
                              fontWeight: 'bold',
                              fontSize: '0.9em'
                            }}>
                              {porcentaje.toFixed(1)}%
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(parseFloat(recurso.precio_unitario_costo || 0))}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(parseFloat(recurso.precio_unitario_venta || 0))}</td>
                    <td style={{ textAlign: 'right' }}>{parseFloat(recurso.markup || 0).toFixed(2)}%</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(parseFloat(recurso.subtotal_costo || 0))}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(parseFloat(recurso.subtotal || 0))}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(parseFloat(recurso.total_ejecutado_costo || 0))}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(parseFloat(recurso.total_gastado_costo || 0))}</td>
                    <td>
                      <span
                        className="estado-badge"
                        style={{
                          backgroundColor: recurso.status === 'activo' ? '#10b981' : '#ef4444',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.85em'
                        }}
                      >
                        {recurso.status === 'activo' ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <FiPackage size={48} />
            <p>No hay recursos en los presupuestos de costo de este proyecto</p>
          </div>
        )}
      </div>
    );
  };

  const renderOrdenesCompra = () => (
    <div className="tab-content-ordenes">
      <div className="section-header">
        <h3>Órdenes de Compra</h3>
      </div>

      {loading ? (
        <Loading fullScreen={false} size="sm" mensaje="Cargando órdenes de compra" />
      ) : ordenesCompra.length > 0 ? (
        <div className="ordenes-table">
          <table>
            <thead>
              <tr>
                <th style={{ color: '#fff' }}>N° OC</th>
                <th style={{ color: '#fff' }}>Proveedor</th>
                <th style={{ color: '#fff' }}>Estado</th>
                <th style={{ color: '#fff' }}>Fecha Emisión</th>
                <th style={{ color: '#fff' }}>Total</th>
                <th style={{ color: '#fff' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {ordenesCompra.map((orden) => (
                <tr key={orden.id}>
                  <td>
                    <strong>{orden.numero_oc}</strong>
                  </td>
                  <td>{orden.proveedor_nombre || '-'}</td>
                  <td>
                    <span
                      className="estado-badge"
                      style={{ backgroundColor: estadoColors[orden.estado] }}
                    >
                      {getEstadoLabel(orden.estado)}
                    </span>
                  </td>
                  <td>{formatDate(orden.fecha_emision)}</td>
                  <td>{formatCurrency(orden.total_oc)}</td>
                  <td className="acciones">
                    <button
                      className="btn-icon"
                      onClick={() => handleVerOrdenCompra(orden)}
                      title="Ver Orden"
                    >
                      <FiEye />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleEditarOrdenCompra(orden)}
                      title="Editar Orden"
                    >
                      <FiEdit />
                    </button>
                    <select
                      value={orden.estado}
                      onChange={(e) => handleCambiarEstadoOrden(orden.id, e.target.value)}
                      className="estado-select"
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="emitida">Emitida</option>
                      <option value="recibida">Recibida</option>
                      <option value="facturada">Facturada</option>
                      <option value="cancelada">Cancelada</option>
                    </select>
                    <button
                      className="btn-icon danger"
                      onClick={() => handleEliminarOrdenCompra(orden)}
                      title="Eliminar Orden"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <FiShoppingCart size={48} />
          <p>No hay órdenes de compra para este proyecto</p>
        </div>
      )}
    </div>
  );

  const renderBodega = () => {
    const handleNuevoMovimiento = () => {
      setMovimientoEditando(null);
      setShowCrearMovimientoBodega(true);
    };

    const handleEditarMovimiento = (movimiento) => {
      setMovimientoEditando(movimiento);
      setShowCrearMovimientoBodega(true);
    };

    const handleEliminarMovimiento = async (movimientoId) => {
      if (!window.confirm('¿Está seguro de eliminar este movimiento? Esta acción no se puede deshacer.')) {
        return;
      }

      try {
        await bodegaService.deleteMovimiento(movimientoId);
        toast.success('Movimiento eliminado correctamente');
        cargarMovimientosBodega();
      } catch (error) {
        console.error('Error al eliminar movimiento:', error);
        toast.error('Error al eliminar movimiento');
      }
    };

    const handleCompletarMovimiento = async (movimientoId) => {
      if (!window.confirm('¿Está seguro de completar este movimiento? Esto afectará el inventario.')) {
        return;
      }

      try {
        await bodegaService.updateMovimiento(movimientoId, { estado: 'completado' });
        toast.success('Movimiento completado exitosamente. El inventario ha sido actualizado.');
        cargarMovimientosBodega();
      } catch (error) {
        console.error('Error al completar movimiento:', error);
        toast.error('Error al completar movimiento');
      }
    };

    // Filtrar movimientos según los filtros activos
    const movimientosFiltrados = movimientosBodega.filter(mov => {
      const matchBodega = !filtrosBodega.bodega || mov.bodega === parseInt(filtrosBodega.bodega);
      const matchTipo = !filtrosBodega.tipo_movimiento || mov.tipo_movimiento === filtrosBodega.tipo_movimiento;
      const matchMotivo = !filtrosBodega.motivo || mov.motivo === filtrosBodega.motivo;
      const matchDocumento = !filtrosBodega.tipo_documento || mov.tipo_documento === filtrosBodega.tipo_documento;
      return matchBodega && matchTipo && matchMotivo && matchDocumento;
    });

    // Obtener lista única de bodegas de los movimientos
    const bodegasEnMovimientos = [...new Set(movimientosBodega.map(m => m.bodega))];

    return (
      <div style={{ padding: '20px' }}>
        {/* Header con botón */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0 }}>Movimientos de Bodega</h3>
          <button
            onClick={handleNuevoMovimiento}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            ➕ Nuevo Movimiento
          </button>
        </div>

        {/* Filtros */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
              Bodega
            </label>
            <select
              value={filtrosBodega.bodega}
              onChange={(e) => setFiltrosBodega({...filtrosBodega, bodega: e.target.value})}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="">Todas las bodegas</option>
              {bodegasEnMovimientos.map(bodegaId => {
                const mov = movimientosBodega.find(m => m.bodega === bodegaId);
                return <option key={bodegaId} value={bodegaId}>{mov?.bodega_nombre}</option>;
              })}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
              Tipo de Movimiento
            </label>
            <select
              value={filtrosBodega.tipo_movimiento}
              onChange={(e) => setFiltrosBodega({...filtrosBodega, tipo_movimiento: e.target.value})}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="">Todos los tipos</option>
              <option value="entrada">Entrada</option>
              <option value="salida">Salida</option>
              <option value="ajuste">Ajuste</option>
              <option value="traspaso">Traspaso</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
              Motivo
            </label>
            <select
              value={filtrosBodega.motivo}
              onChange={(e) => setFiltrosBodega({...filtrosBodega, motivo: e.target.value})}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="">Todos los motivos</option>
              <option value="compra">Compra</option>
              <option value="devolucion">Devolución</option>
              <option value="produccion">Producción</option>
              <option value="venta">Venta</option>
              <option value="uso_interno">Uso Interno</option>
              <option value="merma">Merma</option>
              <option value="ajuste_inventario">Ajuste de Inventario</option>
              <option value="traspaso_bodega">Traspaso entre Bodegas</option>
              <option value="otro">Otro</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
              Tipo Documento
            </label>
            <select
              value={filtrosBodega.tipo_documento}
              onChange={(e) => setFiltrosBodega({...filtrosBodega, tipo_documento: e.target.value})}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="">Todos los documentos</option>
              <option value="factura">Factura</option>
              <option value="orden_compra">Orden de compra</option>
              <option value="guia_despacho">Guía de despacho</option>
              <option value="boleta">Boleta</option>
            </select>
          </div>

          {(filtrosBodega.bodega || filtrosBodega.tipo_movimiento || filtrosBodega.motivo || filtrosBodega.tipo_documento) && (
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                onClick={() => setFiltrosBodega({ bodega: '', tipo_movimiento: '', motivo: '', tipo_documento: '' })}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.875rem'
                }}
              >
                🗑️ Limpiar Filtros
              </button>
            </div>
          )}
        </div>

        {/* Tabla */}
        {loading ? (
          <Loading fullScreen={false} size="sm" mensaje="Cargando movimientos" />
        ) : (
          <div className="movimientos-table-container">
            <table className="movimientos-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Bodega</th>
                  <th>Items</th>
                  <th>Motivo</th>
                  <th>Tipo Documento</th>
                  <th>Doc.</th>
                  <th>Usuario</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {movimientosFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="empty-row">
                      {movimientosBodega.length === 0 ? 'No hay movimientos registrados' : 'No hay movimientos que coincidan con los filtros'}
                    </td>
                  </tr>
                ) : (
                  movimientosFiltrados.map((mov) => (
                    <tr key={mov.id}>
                      <td>{new Date(mov.fecha_movimiento).toLocaleString()}</td>
                      <td>
                        <span className={`badge-tipo ${mov.tipo_movimiento}`}>
                          {mov.tipo_movimiento_display}
                        </span>
                      </td>
                      <td>
                        <span className={`badge-status ${mov.status}`}>
                          {mov.status_display}
                        </span>
                      </td>
                      <td>
                        {mov.bodega_nombre}
                        {mov.bodega_destino_nombre && ` → ${mov.bodega_destino_nombre}`}
                      </td>
                      <td>
                        {mov.detalles && mov.detalles.length > 0 ? (
                          <div className="detalles-summary">
                            {mov.detalles.length} recurso{mov.detalles.length > 1 ? 's' : ''}
                            <details className="detalles-dropdown">
                              <summary>Ver detalles</summary>
                              <ul>
                                {mov.detalles.map((det, idx) => (
                                  <li key={idx}>
                                    {det.recurso_nombre}: {parseFloat(det.cantidad).toFixed(2)} {det.recurso_unidad}
                                    {det.precio_unitario > 0 && ` - $${parseFloat(det.precio_unitario).toLocaleString()}`}
                                  </li>
                                ))}
                              </ul>
                            </details>
                          </div>
                        ) : (
                          <span>Sin detalles</span>
                        )}
                      </td>
                      <td>{mov.motivo_display}</td>
                      <td>{mov.tipo_documento_display || '-'}</td>
                      <td>{mov.numero_documento || '-'}</td>
                      <td>{mov.created_by_nombre}</td>
                      <td>
                        <div style={{display: 'flex', gap: '5px', justifyContent: 'center'}}>
                          {mov.estado === 'borrador' && (
                            <button
                              className="btn-edit-small"
                              onClick={() => handleCompletarMovimiento(mov.id)}
                              title="Completar"
                              style={{backgroundColor: '#10b981', color: 'white'}}
                            >
                              ✓
                            </button>
                          )}
                          <button
                            className="btn-edit-small"
                            onClick={() => handleEditarMovimiento(mov)}
                            title="Editar"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-delete-small"
                            onClick={() => handleEliminarMovimiento(mov.id)}
                            title="Eliminar"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderDocumentos = () => {
    const estadoRendicionColors = {
      pendiente_aprobacion: '#f59e0b',
      aprobado: '#10b981',
      rechazado: '#ef4444',
      reembolsado: '#3b82f6',
    };

    const estadoRendicionLabels = {
      pendiente_aprobacion: 'Pendiente Aprobación',
      aprobado: 'Aprobado',
      rechazado: 'Rechazado',
      reembolsado: 'Reembolsado',
    };

    const tipoComprobanteLabels = {
      factura: 'Factura',
      boleta: 'Boleta',
      recibo: 'Recibo',
      otro: 'Otro',
    };

    return (
      <div className="tab-content-documentos">
        <div className="section-header">
          <h3>Rendiciones / Documentos</h3>
          <button
            className="btn-primary"
            onClick={handleCrearRendicion}
          >
            <FiPlus /> Nueva Rendición
          </button>
        </div>

        {loading ? (
          <Loading fullScreen={false} size="sm" mensaje="Cargando rendiciones" />
        ) : rendiciones.length > 0 ? (
          <div className="rendiciones-list">
            {rendiciones.map((rendicion) => (
              <div key={rendicion.id} className="rendicion-card">
                <div className="rendicion-header" onClick={() => handleToggleRendicion(rendicion)}>
                  <div className="rendicion-info">
                    <h4>
                      {tipoComprobanteLabels[rendicion.tipo_comprobante]} N° {rendicion.numero_comprobante}
                    </h4>
                    <div className="rendicion-meta">
                      <span className="fecha">
                        {formatDate(rendicion.fecha_comprobante)}
                      </span>
                      {rendicion.proveedor_nombre && (
                        <span className="proveedor">
                          {rendicion.proveedor_nombre}
                        </span>
                      )}
                      <span
                        className="estado-badge"
                        style={{ backgroundColor: estadoRendicionColors[rendicion.estado_aprobacion] }}
                      >
                        {estadoRendicionLabels[rendicion.estado_aprobacion]}
                      </span>
                    </div>
                  </div>
                  <div className="rendicion-total">
                    <div className="total-label">Total</div>
                    <div className="total-amount">{formatCurrency(rendicion.total_rendicion)}</div>
                  </div>
                  <button className="expand-btn">
                    {rendicionExpandida === rendicion.id ? <FiChevronUp /> : <FiChevronDown />}
                  </button>
                </div>

                {rendicionExpandida === rendicion.id && (
                  <div className="rendicion-detalles">
                    {rendicion.descripcion && (
                      <div className="descripcion-section">
                        <strong>Descripción:</strong>
                        <p>{rendicion.descripcion}</p>
                      </div>
                    )}

                    <h5>Detalles del Comprobante:</h5>
                    {rendicion.detalles && rendicion.detalles.length > 0 ? (
                      <table className="detalles-table">
                        <thead>
                          <tr>
                            <th>Descripción</th>
                            <th>Categoría</th>
                            <th>Monto</th>
                            <th>Notas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rendicion.detalles.map((detalle) => (
                            <tr key={detalle.id}>
                              <td>{detalle.descripcion}</td>
                              <td>{detalle.categoria_nombre || '-'}</td>
                              <td>{formatCurrency(detalle.monto)}</td>
                              <td>{detalle.notas || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="empty-text">No hay detalles</p>
                    )}

                    <div className="rendicion-actions">
                      {rendicion.estado_aprobacion === 'pendiente_aprobacion' && (
                        <>
                          <button
                            className="btn-icon success"
                            onClick={() => handleAprobarRendicion(rendicion.id)}
                            title="Aprobar"
                          >
                            <FiCheckCircle /> Aprobar
                          </button>
                          <button
                            className="btn-icon danger"
                            onClick={() => handleRechazarRendicion(rendicion.id)}
                            title="Rechazar"
                          >
                            <FiXCircle /> Rechazar
                          </button>
                        </>
                      )}
                      <button
                        className="btn-icon"
                        onClick={() => handleEditarRendicion(rendicion)}
                        title="Editar"
                      >
                        <FiEdit /> Editar
                      </button>
                      <button
                        className="btn-icon danger"
                        onClick={() => handleEliminarRendicion(rendicion.id)}
                        title="Eliminar"
                      >
                        <FiTrash2 /> Eliminar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <FiFolder size={48} />
            <p>No hay rendiciones registradas</p>
            <button
              className="btn-primary"
              onClick={handleCrearRendicion}
            >
              <FiPlus /> Crear Primera Rendición
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderWorkflowProyectos = () => {
    // Obtener datos de costo workflow por evento
    const costoWorkflowPorEvento = proyecto.costo_workflow_por_evento || {
      costo_total: '0',
      horas_totales: 0,
      detalle_por_actividad: []
    };

    return (
      <div className="tab-content-workflow">
        <div className="section-header">
          <h3>Workflow de Proyectos</h3>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
            Costos calculados basados en las tareas de eventos asignadas al proyecto
          </p>
        </div>

        {/* Card General con costo total */}
        <div className="workflow-card-general" style={{
          backgroundColor: '#f0fdf4',
          border: '2px solid #10b981',
          borderRadius: '0.5rem',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#047857' }}>
                General
              </h4>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                Costo total de workflow de eventos
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#047857' }}>
                ${parseFloat(costoWorkflowPorEvento.costo_total).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                {costoWorkflowPorEvento.horas_totales.toFixed(2)} horas totales
              </div>
            </div>
          </div>
        </div>

        {/* Detalle por Actividad */}
        {costoWorkflowPorEvento.detalle_por_actividad && costoWorkflowPorEvento.detalle_por_actividad.length > 0 ? (
          <div className="workflow-actividades">
            <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600' }}>
              Detalle por Actividad/Partida
            </h4>
            {costoWorkflowPorEvento.detalle_por_actividad.map((actividad, actIndex) => (
              <div key={actIndex} className="workflow-actividad-card" style={{
                backgroundColor: 'white',
                border: '2px solid #e5e7eb',
                borderRadius: '0.75rem',
                padding: '1.25rem',
                marginBottom: '1.5rem'
              }}>
                {/* Header de la Actividad */}
                <div style={{
                  marginBottom: '1rem',
                  paddingBottom: '0.75rem',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <h5 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#111827' }}>
                    📋 {actividad.actividad_titulo}
                  </h5>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>
                      {actividad.actividad_fecha_inicio ? `📅 ${new Date(actividad.actividad_fecha_inicio).toLocaleDateString('es-CL')} - ${new Date(actividad.actividad_fecha_termino).toLocaleDateString('es-CL')}` : 'Sin fechas'}
                    </p>
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem' }}>
                      <span style={{ color: '#6b7280' }}>
                        ⏱️ {actividad.horas_totales.toFixed(2)} hrs
                      </span>
                      <span style={{ fontWeight: '600', color: '#10b981' }}>
                        💰 ${parseFloat(actividad.costo_total).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Detalle por Tarea dentro de la Actividad */}
                {actividad.detalle_por_tarea && actividad.detalle_por_tarea.length > 0 ? (
                  <div className="actividad-tareas">
                    {actividad.detalle_por_tarea.map((tarea, tareaIndex) => (
                      <div key={tareaIndex} style={{
                        backgroundColor: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        marginBottom: '1rem'
                      }}>
                        {/* Header de la Tarea */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.75rem',
                          paddingBottom: '0.5rem',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          <h6 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: '600', color: '#374151' }}>
                            ✓ {tarea.tarea_titulo}
                          </h6>
                          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8125rem' }}>
                            <span style={{ color: '#6b7280' }}>
                              {tarea.horas_totales.toFixed(2)} hrs
                            </span>
                            <span style={{ fontWeight: '600', color: '#059669' }}>
                              ${parseFloat(tarea.costo_total).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        </div>

                        {/* Detalle por Usuario dentro de la Tarea */}
                        {tarea.detalle_por_usuario && tarea.detalle_por_usuario.length > 0 ? (
                          <div className="tarea-usuarios" style={{ paddingLeft: '1rem' }}>
                            {tarea.detalle_por_usuario.map((usuario, userIndex) => (
                              <div key={userIndex} style={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1fr 1fr',
                                gap: '0.75rem',
                                alignItems: 'center',
                                paddingBottom: '0.5rem',
                                fontSize: '0.8125rem'
                              }}>
                                <div>
                                  <span style={{ color: '#6b7280', fontWeight: '500' }}>
                                    👤 {usuario.user_name}
                                  </span>
                                </div>
                                <div>
                                  <span style={{ color: '#6b7280' }}>
                                    {usuario.horas.toFixed(2)} hrs
                                  </span>
                                </div>
                                <div>
                                  <span style={{ color: '#6b7280' }}>
                                    ${parseFloat(usuario.costo_empresa).toLocaleString('es-CL')}
                                  </span>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <span style={{ fontWeight: '600', color: '#059669' }}>
                                    ${parseFloat(usuario.costo).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ color: '#9ca3af', fontSize: '0.8125rem', fontStyle: 'italic' }}>
                            Sin usuario asignado
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#9ca3af', fontSize: '0.875rem', fontStyle: 'italic' }}>
                    Sin tareas en esta actividad
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state" style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#9ca3af'
          }}>
            <FiTrendingUp size={48} />
            <p style={{ marginTop: '1rem' }}>
              No hay tareas de eventos con tiempo registrado
            </p>
            <small>
              Los costos se calcularán automáticamente cuando se registre tiempo en las tareas de eventos del proyecto
            </small>
          </div>
        )}
      </div>
    );
  };

  const renderWorkflow = () => {
    // Obtener datos de costo workflow
    const costoWorkflow = proyecto.costo_workflow || { costo_total: '0', horas_totales: 0, detalle_por_post: [] };

    return (
      <div className="tab-content-workflow">
        <div className="section-header">
          <h3>Workflow del Proyecto</h3>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
            Costos calculados basados en las tareas de kanban asignadas al proyecto
          </p>
        </div>

        {/* Card General con costo total */}
        <div className="workflow-card-general" style={{
          backgroundColor: '#f0f9ff',
          border: '2px solid #3b82f6',
          borderRadius: '0.5rem',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#1e40af' }}>
                General
              </h4>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                Costo total de workflow
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e40af' }}>
                ${parseFloat(costoWorkflow.costo_total).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                {costoWorkflow.horas_totales.toFixed(2)} horas totales
              </div>
            </div>
          </div>
        </div>

        {/* Detalle por post */}
        {costoWorkflow.detalle_por_post && costoWorkflow.detalle_por_post.length > 0 ? (
          <div className="workflow-posts">
            <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600' }}>
              Detalle por Post
            </h4>
            {costoWorkflow.detalle_por_post.map((post, postIndex) => (
              <div key={postIndex} className="workflow-post-card" style={{
                backgroundColor: 'white',
                border: '2px solid #e5e7eb',
                borderRadius: '0.75rem',
                padding: '1.25rem',
                marginBottom: '1.5rem'
              }}>
                {/* Header del Post */}
                <div style={{
                  marginBottom: '1rem',
                  paddingBottom: '0.75rem',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <h5 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#111827' }}>
                    📝 {post.post_title}
                  </h5>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>
                      {post.post_publication_date ? `📅 ${new Date(post.post_publication_date).toLocaleDateString('es-CL')}` : 'Sin fecha de publicación'}
                    </p>
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem' }}>
                      <span style={{ color: '#6b7280' }}>
                        ⏱️ {post.horas_totales.toFixed(2)} hrs
                      </span>
                      <span style={{ fontWeight: '600', color: '#3b82f6' }}>
                        💰 ${parseFloat(post.costo_total).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Detalle por usuario dentro del post */}
                <div className="post-users">
                  {post.detalle_por_usuario.map((usuario, userIndex) => (
                    <div key={userIndex} style={{
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      padding: '0.875rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 1fr',
                        gap: '1rem',
                        alignItems: 'center'
                      }}>
                        <div>
                          <label style={{ fontSize: '0.6875rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>
                            Usuario
                          </label>
                          <p style={{ fontSize: '0.875rem', fontWeight: '600', margin: '0.25rem 0 0 0' }}>
                            👤 {usuario.user_name}
                          </p>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.6875rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>
                            Tiempo
                          </label>
                          <p style={{ fontSize: '0.875rem', fontWeight: '600', margin: '0.25rem 0 0 0' }}>
                            {usuario.horas.toFixed(2)} hrs
                          </p>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.6875rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>
                            Costo/mes
                          </label>
                          <p style={{ fontSize: '0.875rem', fontWeight: '600', margin: '0.25rem 0 0 0' }}>
                            ${parseFloat(usuario.costo_empresa).toLocaleString('es-CL')}
                          </p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <label style={{ fontSize: '0.6875rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>
                            Costo
                          </label>
                          <p style={{ fontSize: '0.875rem', fontWeight: '700', color: '#059669', margin: '0.25rem 0 0 0' }}>
                            ${parseFloat(usuario.costo).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state" style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#9ca3af'
          }}>
            <FiTrendingUp size={48} />
            <p style={{ marginTop: '1rem' }}>
              No hay tareas de kanban asignadas a este proyecto
            </p>
            <small>
              Los costos se calcularán automáticamente cuando se asignen tareas de kanban al proyecto
            </small>
          </div>
        )}

        {/* Detalle por Evento */}
        {eventos && eventos.length > 0 && (
          <div className="workflow-eventos" style={{ marginTop: '2rem' }}>
            <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600' }}>
              Detalle por Evento
            </h4>
            {eventos.map((evento, eventoIndex) => {
              const costoEventoWorkflow = evento.costo_workflow || { costo_total: '0', horas_totales: 0, detalle_por_actividad: [] };
              const tieneActividades = costoEventoWorkflow.detalle_por_actividad && costoEventoWorkflow.detalle_por_actividad.length > 0;

              return (
                <div key={eventoIndex} className="workflow-evento-card" style={{
                  backgroundColor: 'white',
                  border: '2px solid #f3e8ff',
                  borderRadius: '0.75rem',
                  padding: '1.25rem',
                  marginBottom: '1.5rem'
                }}>
                  {/* Header del Evento */}
                  <div style={{
                    marginBottom: '1rem',
                    paddingBottom: '0.75rem',
                    borderBottom: '1px solid #f3e8ff'
                  }}>
                    <h5 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#111827' }}>
                      📅 {evento.title || 'Evento sin título'}
                    </h5>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                      <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                        {evento.fecha_inicio ? `Inicio: ${new Date(evento.fecha_inicio).toLocaleDateString('es-CL')}` : 'Sin fecha de inicio'}
                        {evento.fecha_termino && ` - Término: ${new Date(evento.fecha_termino).toLocaleDateString('es-CL')}`}
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem' }}>
                        <span style={{ color: '#6b7280' }}>
                          ⏱️ {costoEventoWorkflow.horas_totales.toFixed(2)} hrs
                        </span>
                        <span style={{ fontWeight: '600', color: '#69B32D' }}>
                          💰 ${parseFloat(costoEventoWorkflow.costo_total).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Detalle por Actividad dentro del evento */}
                  {tieneActividades ? (
                    <div className="evento-actividades">
                      {costoEventoWorkflow.detalle_por_actividad.map((actividad, actIdx) => (
                        <div key={actIdx} style={{
                          backgroundColor: '#faf5ff',
                          border: '1px solid #f3e8ff',
                          borderRadius: '0.5rem',
                          padding: '0.875rem',
                          marginBottom: '0.75rem'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '0.5rem'
                          }}>
                            <strong style={{ fontSize: '0.9375rem', color: '#102826' }}>
                              📋 {actividad.actividad_titulo}
                            </strong>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#69B32D' }}>
                              ${parseFloat(actividad.costo_total).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          <small style={{ color: '#888', display: 'block', marginBottom: '0.5rem' }}>
                            {actividad.horas_totales.toFixed(2)} horas
                          </small>

                          {/* Detalle por Usuario en la actividad */}
                          {actividad.detalle_por_usuario && actividad.detalle_por_usuario.length > 0 && (
                            <div style={{
                              paddingTop: '0.5rem',
                              borderTop: '1px solid #f3e8ff',
                              fontSize: '0.8125rem'
                            }}>
                              {actividad.detalle_por_usuario.map((usuario, userIdx) => (
                                <div key={userIdx} style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  paddingTop: '0.25rem',
                                  color: '#666'
                                }}>
                                  <span>👤 {usuario.user_name} ({usuario.horas.toFixed(2)}h)</span>
                                  <span>${parseFloat(usuario.costo).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#9ca3af', fontSize: '0.875rem', fontStyle: 'italic' }}>
                      Sin tareas de evento
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderAvancePorGastar = () => {
    if (loadingAvance) {
      return <Loading fullScreen={false} size="sm" mensaje="Cargando información de ejecución" />;
    }

    if (!resumenEjecucion) {
      return (
        <div className="empty-state">
          <p>No hay presupuesto de venta activo para este proyecto</p>
        </div>
      );
    }

    const formatCurrency = (value) => {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value || 0);
    };

    // 🆕 Calcular Costo Real Total (Subtotal Presupuesto Costos + Workflow de Kanban + Workflow de Proyectos)
    const costoRealTotal = calcularTotalGastado();

    // Calcular Utilidad Actual Total (Total Ejecutado - Total Gastado)
    // Usar el mismo cálculo que en Presupuesto Costo
    const totalEjecutado = presupuestosCosto.length > 0
      ? (totalEjecutadoPresupuestos[presupuestosCosto[0].id] || 0)
      : 0;
    const utilidadActualTotal = totalEjecutado - costoRealTotal;

    // Calcular porcentaje de Utilidad Actual
    const utilidadActualPorcentaje = totalEjecutado > 0
      ? ((utilidadActualTotal / totalEjecutado) * 100).toFixed(2)
      : '0.00';

    // Calcular Proyección Total = Costo Esperado + Proyección Disponible
    const costoEsperado = presupuestosCosto.length > 0
      ? (parseFloat(presupuestosCosto[0]?.total_presupuestado || presupuestosCosto[0]?.total || 0))
      : 0;
    const proyeccionDisponible = presupuestosCosto.length > 0
      ? (proyeccionPresupuestos[presupuestosCosto[0].id] || 0)
      : 0;
    const proyeccionTotal = costoEsperado + proyeccionDisponible;

    return (
      <div className="avance-por-gastar-container">
        {/* Grid de métricas financieras */}
        <div className="presupuesto-metricas-grid">
          <div className="metrica-card">
            <div className="metrica-icon">📋</div>
            <div className="metrica-info">
              <div className="metrica-label">Costo Presupuestado</div>
              <div className="metrica-valor">{formatCurrency(parseFloat(presupuestosVenta[0]?.subtotal_costo) || 0)}</div>
            </div>
          </div>

          <div className="metrica-card">
            <div className="metrica-icon">💼</div>
            <div className="metrica-info">
              <div className="metrica-label">Costo Esperado</div>
              <div className="metrica-valor">{formatCurrency(presupuestosCosto[0]?.total_presupuestado || presupuestosCosto[0]?.total || 0)}</div>
            </div>
          </div>

          <div className="metrica-card">
            <div className="metrica-icon">✅</div>
            <div className="metrica-info">
              <div className="metrica-label">Total Ejecutado</div>
              <div className="metrica-valor ejecutado">{formatCurrency(totalEjecutado)}</div>
            </div>
          </div>

          <div className="metrica-card">
            <div className="metrica-icon">💸</div>
            <div className="metrica-info">
              <div className="metrica-label">Total Gastado</div>
              <div className="metrica-valor gastado">{formatCurrency(costoRealTotal)}</div>
              <div className="metrica-sublabel">
                Presupuesto + Workflows
              </div>
            </div>
          </div>

          <div className="metrica-card highlight">
            <div className="metrica-icon">💰</div>
            <div className="metrica-info">
              <div className="metrica-label">DESVIACIÓN</div>
              <div className={`metrica-valor ${utilidadActualTotal >= 0 ? 'positivo' : 'negativo'}`}>
                {formatCurrency(utilidadActualTotal)}
              </div>
              <div className="metrica-sublabel">
                <span className={utilidadActualTotal >= 0 ? 'positivo' : 'negativo'}>
                  {utilidadActualPorcentaje}%
                </span>
              </div>
            </div>
          </div>

          <div className="metrica-card">
            <div className="metrica-icon">🔖</div>
            <div className="metrica-info">
              <div className="metrica-label">Por Gastar</div>
              <div className="metrica-valor">{formatCurrency(proyeccionDisponible)}</div>
              <div className="metrica-sublabel">
                Recursos disponibles
              </div>
            </div>
          </div>

          <div className="metrica-card">
            <div className="metrica-icon">📈</div>
            <div className="metrica-info">
              <div className="metrica-label">Proyección</div>
              <div className="metrica-valor">{formatCurrency(proyeccionTotal)}</div>
              <div className="metrica-sublabel">
                Costo Esperado + Disponible
              </div>
            </div>
          </div>
        </div>

        {/* Gráfico de Barras */}
        <div className="chart-section" style={{
          marginTop: '2rem',
          marginBottom: '2rem',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid rgba(148, 163, 184, 0.1)'
        }}>
          <h3 style={{
            marginBottom: '1.5rem',
            color: '#1e293b',
            fontSize: '1.3rem',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>

          </h3>
          <ResponsiveContainer width="100%" height={450}>
            <BarChart
              data={[
                {
                  name: 'Costo Presupuestado',
                  valor: parseFloat(presupuestosVenta[0]?.subtotal_costo) || 0
                },
                {
                  name: 'Costo Esperado',
                  valor: presupuestosCosto[0]?.total_presupuestado || presupuestosCosto[0]?.total || 0
                },
                {
                  name: 'Total Ejecutado',
                  valor: totalEjecutado
                },
                {
                  name: 'Total Gastado',
                  valor: costoRealTotal
                },
                {
                  name: 'DESVIACIÓN',
                  valor: utilidadActualTotal
                },
                {
                  name: 'Por Gastar',
                  valor: proyeccionDisponible
                },
                {
                  name: 'Proyección',
                  valor: proyeccionTotal
                }
              ]}
              margin={{ top: 20, right: 40, left: 60, bottom: 90 }}
            >
              <defs>
                <linearGradient id="colorPurple" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#6D28D9" stopOpacity={0.8}/>
                </linearGradient>
                <linearGradient id="colorIndigo" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366F1" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#4F46E5" stopOpacity={0.8}/>
                </linearGradient>
                <linearGradient id="colorGreen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#059669" stopOpacity={0.8}/>
                </linearGradient>
                <linearGradient id="colorOrange" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F59E0B" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#D97706" stopOpacity={0.8}/>
                </linearGradient>
                <linearGradient id="colorRed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#EF4444" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#DC2626" stopOpacity={0.8}/>
                </linearGradient>
                <linearGradient id="colorBlue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0.8}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E1" opacity={0.5} />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={100}
                interval={0}
                tick={{ fill: '#475569', fontSize: 13, fontWeight: 500 }}
                stroke="#94A3B8"
              />
              <YAxis
                tick={{ fill: '#475569', fontSize: 13, fontWeight: 500 }}
                stroke="#94A3B8"
                tickFormatter={(value) => {
                  const absValue = Math.abs(value);
                  const sign = value < 0 ? '-' : '';
                  if (absValue >= 1000000) {
                    return `${sign}$${(absValue / 1000000).toFixed(1)}M`;
                  } else if (absValue >= 1000) {
                    return `${sign}$${(absValue / 1000).toFixed(0)}K`;
                  }
                  return `${sign}$${absValue}`;
                }}
              />
              <Tooltip
                cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }}
                formatter={(value) => formatCurrency(value)}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  fontWeight: '600'
                }}
                labelStyle={{
                  color: '#1e293b',
                  fontWeight: '700',
                  marginBottom: '4px'
                }}
              />
              <Bar
                dataKey="valor"
                radius={10}
                maxBarSize={80}
                onClick={(data) => {
                  if (data && data.name === 'Total Gastado') {
                    setShowModalDetalleCostos(true);
                  }
                }}
                cursor="pointer"
              >
                {[
                  {
                    name: 'Costo Presupuestado',
                    valor: parseFloat(presupuestosVenta[0]?.subtotal_costo) || 0,
                    gradient: 'url(#colorPurple)'
                  },
                  {
                    name: 'Costo Esperado',
                    valor: presupuestosCosto[0]?.total_presupuestado || presupuestosCosto[0]?.total || 0,
                    gradient: 'url(#colorIndigo)'
                  },
                  {
                    name: 'Total Ejecutado',
                    valor: totalEjecutado,
                    gradient: 'url(#colorGreen)'
                  },
                  {
                    name: 'Total Gastado',
                    valor: costoRealTotal,
                    gradient: 'url(#colorOrange)'
                  },
                  {
                    name: 'DESVIACIÓN',
                    valor: utilidadActualTotal,
                    gradient: utilidadActualTotal >= 0 ? 'url(#colorGreen)' : 'url(#colorRed)'
                  },
                  {
                    name: 'Por Gastar',
                    valor: proyeccionDisponible,
                    gradient: proyeccionDisponible >= 0 ? 'url(#colorBlue)' : 'url(#colorRed)'
                  },
                  {
                    name: 'Proyección',
                    valor: proyeccionTotal,
                    gradient: proyeccionTotal >= 0 ? 'url(#colorPurple)' : 'url(#colorRed)'
                  }
                ].map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.valor < 0 ? 'url(#colorRed)' : entry.gradient}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderGantt = () => {
    // Si no hay eventos, mostrar mensaje
    if (!eventos || eventos.length === 0) {
      return (
        <div className="empty-state">
          <p>No hay eventos asociados a este proyecto</p>
        </div>
      );
    }

    // Mostrar el Gantt del primer evento
    const primerEvento = eventos[0];

    return (
      <div className="gantt-container">
        <GanttChart eventoId={primerEvento.id} />
      </div>
    );
  };

  // Funciones para la pestaña de Tareas
  const loadTareasDelProyecto = async () => {
    try {
      setLoadingTareas(true);
      const response = await eventosGanttService.tareas.listByProyecto(proyecto.id);
      const tareasData = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setTareasDelProyecto(tareasData);
      console.log('📋 Tareas del proyecto cargadas:', tareasData.length);
    } catch (error) {
      console.error('Error loading tareas:', error);
      setTareasDelProyecto([]);
    } finally {
      setLoadingTareas(false);
    }
  };

  const cargarRecursosDeTarea = async (tarea) => {
    if (!tarea || !tarea.id) {
      console.log('❌ No hay tarea o tarea.id');
      setRecursosDisponiblesTarea([]);
      return;
    }

    try {
      console.log('🔍 Cargando recursos para tarea:', tarea.id);
      const response = await eventosGanttService.tareas.retrieve(tarea.id);
      const tareaDetalle = response.data;

      if (tareaDetalle.presupuesto_costo_recursos_detalle && tareaDetalle.presupuesto_costo_recursos_detalle.length > 0) {
        const recursosManoDeObra = tareaDetalle.presupuesto_costo_recursos_detalle.filter(r => {
          const tipoRecurso = r.recurso_referencia_data?.tipo_recurso?.nombre;
          return tipoRecurso === 'Mano de obra';
        });

        setRecursosDisponiblesTarea(recursosManoDeObra);
        setManualTimeForm(prev => ({
          ...prev,
          presupuesto_costo_recursos: recursosManoDeObra.length === 1 ? [recursosManoDeObra[0].id] : []
        }));
      } else {
        setRecursosDisponiblesTarea([]);
      }
    } catch (error) {
      console.error('Error al cargar recursos:', error);
      setRecursosDisponiblesTarea([]);
    }
  };

  const handleAddManualTime = async (e) => {
    e.preventDefault();

    if (!manualTimeForm.fecha.trim()) {
      alert('La fecha es requerida');
      return;
    }

    if (!manualTimeForm.minutos || parseFloat(manualTimeForm.minutos) <= 0) {
      alert('Por favor ingresa un tiempo válido en minutos (mayor a 0)');
      return;
    }

    try {
      const timelogData = {
        tarea: selectedTareaForTime.id,
        fecha: manualTimeForm.fecha,
        minutos: parseInt(manualTimeForm.minutos),
        notas: manualTimeForm.notas,
        presupuesto_costo_recursos: manualTimeForm.presupuesto_costo_recursos
      };

      console.log('📤 Enviando TimeLog con datos:', timelogData);
      await eventosGanttService.timelogs.create(timelogData);
      console.log('✅ TimeLog creado exitosamente');

      // Recalcular horas de la tarea
      await eventosGanttService.tareas.recalcularHoras(selectedTareaForTime.id);
      console.log('✅ Horas recalculadas para la tarea');

      // Pequeño delay para asegurar que el backend procese la actualización
      await new Promise(resolve => setTimeout(resolve, 300));

      // Recargar tareas del proyecto
      await loadTareasDelProyecto();
      console.log('✅ Tareas del proyecto recargadas');

      // Si se asignaron recursos, recargar presupuestos y recursos
      if (manualTimeForm.presupuesto_costo_recursos.length > 0) {
        // Recargar presupuestos de costo para actualizar Total Ejecutado
        await cargarPresupuestosCosto();
        console.log('✅ Presupuestos de costo recargados - Total Ejecutado actualizado');

        // Recargar recursos del proyecto para actualizar el % de avance
        if (recursosProyecto.length > 0) {
          console.log('🔄 Recargando recursos del proyecto para actualizar avance...');
          await cargarRecursosProyecto();
          console.log('✅ Recursos del proyecto actualizados');
        }
      }

      alert('✅ Tiempo registrado correctamente');

      setShowManualTimeForm(false);
      setSelectedTareaForTime(null);
      setRecursosDisponiblesTarea([]);
      setManualTimeForm({
        fecha: new Date().toISOString().split('T')[0],
        minutos: '',
        notas: '',
        presupuesto_costo_recursos: []
      });

      window.dispatchEvent(new CustomEvent('tareaTimeLogAdded', { detail: { tareaId: selectedTareaForTime.id } }));
    } catch (error) {
      console.error('Error al agregar tiempo:', error);
      const errorMsg = error.response?.data?.detail ||
                      Object.values(error.response?.data || {}).flat().join(', ') ||
                      'Error al agregar tiempo';
      alert(errorMsg);
    }
  };

  const handleDeleteTareaEvento = async (tareaId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta tarea? Esto también eliminará todos los TimeLogs asociados y recalculará el avance de los recursos.')) {
      return;
    }

    try {
      console.log('🗑️ Eliminando TareaEvento:', tareaId);
      await eventosService.deleteTareaEvento(tareaId);
      console.log('✅ TareaEvento eliminada exitosamente');

      // Pequeño delay para asegurar que el backend procese completamente la eliminación
      await new Promise(resolve => setTimeout(resolve, 500));

      // Recargar tareas del proyecto
      await loadTareasDelProyecto();
      console.log('✅ Tareas del proyecto recargadas');

      // Recargar presupuestos de costo para actualizar Total Ejecutado y Total Gastado
      await cargarPresupuestosCosto();
      console.log('✅ Presupuestos de costo recargados - Totales actualizados');

      // Recargar recursos del proyecto para actualizar el % de avance
      if (recursosProyecto.length > 0) {
        console.log('🔄 Recargando recursos del proyecto para actualizar avance...');
        await cargarRecursosProyecto();
        console.log('✅ Recursos del proyecto actualizados');
      }

      alert('✅ Tarea eliminada exitosamente. Los totales del proyecto han sido actualizados.');
    } catch (error) {
      console.error('❌ Error al eliminar tarea:', error);
      alert(error.response?.data?.error || 'Error al eliminar la tarea');
    }
  };

  // Efecto para cargar tareas cuando se cambia a la pestaña "Tareas"
  useEffect(() => {
    if (activeTab === 'tareas' && proyecto.id) {
      console.log('🔄 Cambiando a pestaña Tareas - Cargando datos...');
      loadTareasDelProyecto();
    }
  }, [activeTab, proyecto.id]);

  const renderTareas = () => {
    return (
      <div className="tasks-section">
        <div className="section-header">
          <h3>📋 Tareas del Proyecto ({tareasDelProyecto.length})</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {tareasDelProyecto.length > 0 && (() => {
              const totalHoras = tareasDelProyecto.reduce((sum, tarea) => sum + (parseFloat(tarea.total_horas) || 0), 0);
              const horas = Math.floor(totalHoras);
              const minutos = Math.round((totalHoras - horas) * 60);
              const tiempoFormato = `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}:00`;
              return (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: '#dbeafe',
                  color: '#1e40af',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}>
                  <span>⏱️</span>
                  <span>Registrado: {tiempoFormato}</span>
                </div>
              );
            })()}
          </div>
        </div>

        {loadingTareas ? (
          <div style={{ background: '#f9fafb', borderRadius: '0.5rem', padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            <Loading fullScreen={false} size="sm" mensaje="Cargando tareas" />
          </div>
        ) : tareasDelProyecto.length === 0 ? (
          <div style={{ background: '#f9fafb', borderRadius: '0.5rem', padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>No hay tareas asociadas a este proyecto</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {tareasDelProyecto.map(tarea => (
              <div
                key={tarea.id}
                style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#111827' }}>{tarea.titulo}</h4>
                  {tarea.estado_kanban_data && (
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      background: tarea.estado_kanban_data.color || '#f3f4f6',
                      color: '#fff'
                    }}>
                      {tarea.estado_kanban_data.nombre}
                    </span>
                  )}
                </div>

                {tarea.descripcion && (
                  <p style={{ margin: '0.5rem 0', fontSize: '0.875rem', color: '#6b7280', lineHeight: '1.5' }}>
                    {tarea.descripcion}
                  </p>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>
                  {/* Usuario asignado */}
                  {tarea.usuario_asignado_nombre && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.5rem',
                      background: '#f0f9ff',
                      borderRadius: '6px'
                    }}>
                      <span>👤</span>
                      <span style={{ fontWeight: '500' }}>{tarea.usuario_asignado_nombre}</span>
                    </div>
                  )}

                  {/* Equipo asignado (si no hay usuario asignado) */}
                  {!tarea.usuario_asignado_nombre && tarea.equipo_asignado_nombre && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.5rem',
                      background: tarea.equipo_asignado_color || '#f3f4f6',
                      borderRadius: '6px',
                      color: '#fff'
                    }}>
                      <span>👥</span>
                      <span style={{ fontWeight: '500' }}>{tarea.equipo_asignado_nombre}</span>
                    </div>
                  )}

                  {/* Fecha de inicio */}
                  {tarea.fecha_inicio && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.5rem',
                      background: '#f0fdf4',
                      borderRadius: '6px'
                    }}>
                      <span>🚀</span>
                      <span style={{ fontWeight: '500' }}>
                        Inicio: {(() => {
                          const [year, month, day] = tarea.fecha_inicio.split('-').map(Number);
                          const date = new Date(year, month - 1, day);
                          return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
                        })()}
                      </span>
                    </div>
                  )}

                  {/* Fecha de término */}
                  {tarea.fecha_termino && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.5rem',
                      background: '#fef2f2',
                      borderRadius: '6px'
                    }}>
                      <span>🏁</span>
                      <span style={{ fontWeight: '500' }}>
                        Fin: {(() => {
                          const [year, month, day] = tarea.fecha_termino.split('-').map(Number);
                          const date = new Date(year, month - 1, day);
                          return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
                        })()}
                      </span>
                    </div>
                  )}

                  {/* Badge de tiempo registrado */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    marginLeft: 'auto',
                    padding: '0.25rem 0.75rem',
                    background: parseFloat(tarea.total_horas) > 0 ? '#dbeafe' : '#f3f4f6',
                    color: parseFloat(tarea.total_horas) > 0 ? '#1e40af' : '#6b7280',
                    borderRadius: '12px',
                    fontWeight: '600',
                    fontSize: '0.8rem'
                  }}>
                    <i className="fas fa-clock"></i>
                    <span>{parseFloat(tarea.total_horas || 0).toFixed(2)}h</span>
                  </div>
                </div>

                <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.5rem', justifyContent: 'space-between' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTareaForTime(tarea);
                      setShowManualTimeForm(true);
                      cargarRecursosDeTarea(tarea);
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'linear-gradient(135deg, #102826, #69B32D)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      flex: 1
                    }}
                  >
                    <i className="fas fa-clock"></i> Agregar Tiempo Manual
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTareaEvento(tarea.id);
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    🗑️ Eliminar Tarea
                  </button>
                </div>

                {showManualTimeForm && selectedTareaForTime && selectedTareaForTime.id === tarea.id && (
                  <div style={{ background: '#f0f9ff', border: '2px solid #3b82f6', borderRadius: '8px', padding: '1.5rem', marginTop: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h4 style={{ margin: 0, color: '#1e40af', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i className="fas fa-clock"></i> Agregar Tiempo Manual
                      </h4>
                      <button
                        onClick={() => {
                          setShowManualTimeForm(false);
                          setSelectedTareaForTime(null);
                          setRecursosDisponiblesTarea([]);
                          setManualTimeForm({
                            fecha: new Date().toISOString().split('T')[0],
                            minutos: '',
                            notas: '',
                            presupuesto_costo_recursos: []
                          });
                        }}
                        style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280', padding: '0', lineHeight: '1' }}
                      >
                        ×
                      </button>
                    </div>

                    <form onSubmit={handleAddManualTime}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#374151', fontSize: '0.875rem', fontWeight: '600' }}>
                            Fecha
                          </label>
                          <input
                            type="date"
                            value={manualTimeForm.fecha}
                            onChange={(e) => setManualTimeForm({ ...manualTimeForm, fecha: e.target.value })}
                            required
                            max={new Date().toISOString().split('T')[0]}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#374151', fontSize: '0.875rem', fontWeight: '600' }}>
                            Tiempo (minutos)
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={manualTimeForm.minutos}
                            onChange={(e) => setManualTimeForm({ ...manualTimeForm, minutos: e.target.value })}
                            required
                            placeholder="ej: 30"
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>

                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: '#374151', fontSize: '0.875rem', fontWeight: '600' }}>
                          Notas (opcional)
                        </label>
                        <textarea
                          value={manualTimeForm.notas}
                          onChange={(e) => setManualTimeForm({ ...manualTimeForm, notas: e.target.value })}
                          placeholder="Describe en qué trabajaste..."
                          rows="3"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }}
                        />
                      </div>

                      {recursosDisponiblesTarea.length > 0 && (
                        <div style={{ marginBottom: '1rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#374151', fontSize: '0.875rem', fontWeight: '600' }}>
                            Recursos Utilizados (opcional)
                            <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '8px', fontWeight: '400' }}>
                              Mantén presionado Ctrl (Cmd en Mac) para seleccionar múltiples
                            </span>
                          </label>
                          <select
                            multiple
                            value={manualTimeForm.presupuesto_costo_recursos}
                            onChange={(e) => {
                              const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                              setManualTimeForm(prev => ({
                                ...prev,
                                presupuesto_costo_recursos: selectedOptions
                              }));
                            }}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', minHeight: '100px', boxSizing: 'border-box' }}
                          >
                            {recursosDisponiblesTarea.map(recurso => (
                              <option key={recurso.id} value={recurso.id}>
                                {recurso.nombre} - ${recurso.precio_unitario_costo?.toLocaleString('es-CL')}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setShowManualTimeForm(false);
                            setSelectedTareaForTime(null);
                            setRecursosDisponiblesTarea([]);
                            setManualTimeForm({
                              fecha: new Date().toISOString().split('T')[0],
                              minutos: '',
                              notas: '',
                              presupuesto_costo_recursos: []
                            });
                          }}
                          style={{ padding: '0.5rem 1rem', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          style={{ padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #102826, #69B32D)', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
                        >
                          Guardar Tiempo
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="modal-overlay-fullscreen" onClick={onClose}>
        <div className="modal-fullscreen" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header-fullscreen">
            <div className="header-info">
              <h2>{proyecto.cliente_nombre} - {proyecto.nombre}</h2>
            </div>
            <button className="close-btn" onClick={onClose}>
              <FiX size={24} />
            </button>
          </div>

          {/* Tabs Navigation */}
          <div className="tabs-navigation">
            <button
              className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
              onClick={() => setActiveTab('general')}
            >
              <FiFileText />
              <span>General</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'presupuesto-venta' ? 'active' : ''}`}
              onClick={() => setActiveTab('presupuesto-venta')}
            >
              <FiDollarSign />
              <span>Presupuesto Venta</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'presupuesto-costo' ? 'active' : ''}`}
              onClick={() => setActiveTab('presupuesto-costo')}
            >
              <FiDollarSign />
              <span>Presupuesto Costo</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'recursos-solicitados' ? 'active' : ''}`}
              onClick={() => setActiveTab('recursos-solicitados')}
            >
              <FiPackage />
              <span>Recursos Solicitados</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'ordenes-compra' ? 'active' : ''}`}
              onClick={() => setActiveTab('ordenes-compra')}
            >
              <FiShoppingCart />
              <span>Órdenes de Compra</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'bodega' ? 'active' : ''}`}
              onClick={() => setActiveTab('bodega')}
            >
              <FiPackage />
              <span>Bodega</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'documentos' ? 'active' : ''}`}
              onClick={() => setActiveTab('documentos')}
            >
              <FiFolder />
              <span>Documentos</span>
            </button>
            {isTabVisible('workflow') && (
              <button
                className={`tab-button ${activeTab === 'workflow' ? 'active' : ''}`}
                onClick={() => setActiveTab('workflow')}
              >
                <FiTrendingUp />
                <span>$ Workflow</span>
              </button>
            )}
            {isTabVisible('workflow-proyectos') && (
              <button
                className={`tab-button ${activeTab === 'workflow-proyectos' ? 'active' : ''}`}
                onClick={() => setActiveTab('workflow-proyectos')}
              >
                <FiTrendingUp />
                <span>$ Workflow Proyectos</span>
              </button>
            )}
            <button
              className={`tab-button ${activeTab === 'avance-por-gastar' ? 'active' : ''}`}
              onClick={() => setActiveTab('avance-por-gastar')}
            >
              <FiTrendingUp />
              <span>Avance por Gastar</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'gantt' ? 'active' : ''}`}
              onClick={() => setActiveTab('gantt')}
            >
              📊
              <span>Gantt</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'tareas' ? 'active' : ''}`}
              onClick={() => setActiveTab('tareas')}
            >
              📋
              <span>Tareas</span>
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content-area">
            {activeTab === 'general' && renderGeneral()}
            {activeTab === 'presupuesto-venta' && renderPresupuestoVenta()}
            {activeTab === 'presupuesto-costo' && renderPresupuestoCosto()}
            {activeTab === 'recursos-solicitados' && renderRecursosSolicitados()}
            {activeTab === 'ordenes-compra' && renderOrdenesCompra()}
            {activeTab === 'bodega' && renderBodega()}
            {activeTab === 'documentos' && renderDocumentos()}
            {activeTab === 'workflow' && renderWorkflow()}
            {activeTab === 'workflow-proyectos' && renderWorkflowProyectos()}
            {activeTab === 'avance-por-gastar' && renderAvancePorGastar()}
            {activeTab === 'gantt' && renderGantt()}
            {activeTab === 'tareas' && renderTareas()}
          </div>
        </div>
      </div>

      {/* Modal Crear/Editar Presupuesto Venta */}
      {showCrearPresupuestoVenta && (
        <div className="modal-overlay" onClick={() => { setShowCrearPresupuestoVenta(false); resetFormPresupuesto(); }}>
          <div className="modal-content modal-presupuesto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{presupuestoEditando ? 'Editar' : 'Nuevo'} Presupuesto de Venta</h2>
              <button className="close-btn" onClick={() => { setShowCrearPresupuestoVenta(false); resetFormPresupuesto(); }}>
                ✕
              </button>
            </div>

            <form onSubmit={handleCrearPresupuestoVenta} className="presupuesto-form-modern">
              {/* Sección: Información General */}
              <div className="pf-section">
                <div className="pf-section-header">
                  <span className="pf-section-icon">📋</span>
                  <h4>Información General</h4>
                </div>
                <div className="pf-section-body">
                  <div className="form-group">
                    <label className="pf-label">Proyecto</label>
                    <input
                      type="text"
                      value={`${proyecto.cliente_nombre} - ${proyecto.nombre}`}
                      disabled
                      className="form-control disabled-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="pf-label">Nombre del Presupuesto</label>
                    <input
                      type="text"
                      name="nombre"
                      value={formDataPresupuesto.nombre || ''}
                      onChange={handleInputChangePresupuesto}
                      placeholder="Ej: Propuesta Inicial, Versión 2, etc."
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label className="pf-label">Descripción</label>
                    <textarea
                      name="descripcion"
                      value={formDataPresupuesto.descripcion}
                      onChange={handleInputChangePresupuesto}
                      rows="3"
                      className="form-control"
                      placeholder="Descripción del presupuesto..."
                    />
                  </div>
                </div>
              </div>

              {/* Sección: Montos y Configuración */}
              <div className="pf-section pf-section-montos">
                <div className="pf-section-header">
                  <span className="pf-section-icon">💰</span>
                  <h4>Montos y Configuración</h4>
                </div>
                <div className="pf-section-body">
                  <div className="pf-grid-2">
                    <div className="form-group">
                      <label className="pf-label">Comisión Agencia (%)</label>
                      <div className="pf-input-decorated">
                        <input
                          type="number"
                          name="comision_agencia_porcentaje"
                          value={formDataPresupuesto.comision_agencia_porcentaje}
                          onChange={handleInputChangePresupuesto}
                          min="0"
                          max="100"
                          step="0.01"
                          className="form-control"
                        />
                        <span className="pf-input-suffix">%</span>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="pf-label">Descuento (%)</label>
                      <div className="pf-input-decorated">
                        <input
                          type="number"
                          name="descuento_porcentaje"
                          value={formDataPresupuesto.descuento_porcentaje}
                          onChange={handleInputChangePresupuesto}
                          min="0"
                          max="100"
                          step="0.01"
                          className="form-control"
                        />
                        <span className="pf-input-suffix">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="pf-iva-group">
                    <label className="pf-toggle-label">
                      <div className="pf-toggle">
                        <input
                          type="checkbox"
                          name="aplicar_iva"
                          checked={formDataPresupuesto.aplicar_iva}
                          onChange={handleInputChangePresupuesto}
                        />
                        <span className="pf-toggle-slider"></span>
                      </div>
                      <span>Aplicar IVA</span>
                    </label>
                    <div className={`form-group pf-iva-input ${!formDataPresupuesto.aplicar_iva ? 'pf-disabled' : ''}`}>
                      <label className="pf-label">IVA (%)</label>
                      <div className="pf-input-decorated">
                        <input
                          type="number"
                          name="iva_porcentaje"
                          value={formDataPresupuesto.iva_porcentaje}
                          onChange={handleInputChangePresupuesto}
                          min="0"
                          max="100"
                          step="0.01"
                          disabled={!formDataPresupuesto.aplicar_iva}
                          className="form-control"
                        />
                        <span className="pf-input-suffix">%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setShowCrearPresupuestoVenta(false); resetFormPresupuesto(); }}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {presupuestoEditando ? 'Actualizar' : 'Crear'} Presupuesto
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Crear/Editar Presupuesto Costo */}
      {showCrearPresupuestoCosto && (
        <div className="modal-overlay" onClick={() => { setShowCrearPresupuestoCosto(false); resetFormPresupuesto(); }}>
          <div className="modal-content modal-presupuesto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{presupuestoEditando ? 'Editar' : 'Nuevo'} Presupuesto de Costo</h2>
              <button className="close-btn" onClick={() => { setShowCrearPresupuestoCosto(false); resetFormPresupuesto(); }}>
                ✕
              </button>
            </div>

            <form onSubmit={handleCrearPresupuestoCosto} className="presupuesto-form-modern">
              {/* Sección: Información General */}
              <div className="pf-section">
                <div className="pf-section-header">
                  <span className="pf-section-icon">📋</span>
                  <h4>Información General</h4>
                </div>
                <div className="pf-section-body">
                  <div className="form-group">
                    <label className="pf-label">Proyecto</label>
                    <input
                      type="text"
                      value={`${proyecto.cliente_nombre} - ${proyecto.nombre}`}
                      disabled
                      className="form-control disabled-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="pf-label">Nombre del Presupuesto</label>
                    <input
                      type="text"
                      name="nombre"
                      value={formDataPresupuesto.nombre || ''}
                      onChange={handleInputChangePresupuesto}
                      placeholder="Ej: Presupuesto Base, Versión Ajustada, etc."
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label className="pf-label">Descripción</label>
                    <textarea
                      name="descripcion"
                      value={formDataPresupuesto.descripcion}
                      onChange={handleInputChangePresupuesto}
                      rows="3"
                      className="form-control"
                      placeholder="Descripción del presupuesto..."
                    />
                  </div>
                </div>
              </div>

              {/* Sección: Montos */}
              <div className="pf-section pf-section-montos">
                <div className="pf-section-header">
                  <span className="pf-section-icon">💰</span>
                  <h4>Montos</h4>
                </div>
                <div className="pf-section-body">
                  <div className="form-group">
                    <label className="pf-label">Comisión Agencia (%)</label>
                    <div className="pf-input-decorated">
                      <input
                        type="number"
                        name="comision_agencia_porcentaje"
                        value={formDataPresupuesto.comision_agencia_porcentaje}
                        onChange={handleInputChangePresupuesto}
                        min="0"
                        max="100"
                        step="0.01"
                        className="form-control"
                      />
                      <span className="pf-input-suffix">%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setShowCrearPresupuestoCosto(false); resetFormPresupuesto(); }}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {presupuestoEditando ? 'Actualizar' : 'Crear'} Presupuesto
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Agregar Item Venta */}
      {presupuestoSeleccionado && (
        <AgregarItemModal
          presupuesto={presupuestoSeleccionado}
          isOpen={showAgregarItemVenta}
          onClose={() => {
            setShowAgregarItemVenta(false);
            setItemParaEditar(null);
          }}
          onItemAdded={handleItemAdded}
          tipoPresupuesto="venta"
          itemParaEditar={itemParaEditar}
        />
      )}

      {/* Modal Agregar Item Costo */}
      {presupuestoSeleccionado && (
        <AgregarItemModal
          presupuesto={presupuestoSeleccionado}
          isOpen={showAgregarItemCosto}
          onClose={() => {
            setShowAgregarItemCosto(false);
            setPresupuestoSeleccionado(null);
          }}
          onItemAdded={handleItemAdded}
          tipoPresupuesto="costo"
        />
      )}

      {/* Modal Crear/Editar Orden de Compra */}
      {showCrearOrdenCompra && presupuestoSeleccionado && (
        <CrearOrdenCompraOneBaseModal
          presupuesto={presupuestoSeleccionado}
          itemsSeleccionados={itemsExpandidos.filter(item => itemsSeleccionados.includes(item.id))}
          ordenCompraEditando={ordenCompraEditando}
          onClose={() => {
            setShowCrearOrdenCompra(false);
            setOrdenCompraEditando(null);
          }}
          onCreated={handleOrdenCompraCreada}
        />
      )}

      {/* Modal Crear/Editar Movimiento de Bodega */}
      {showCrearMovimientoBodega && (
        <CrearMovimientoBodegaModal
          proyecto={proyecto}
          movimientoEditando={movimientoEditando}
          onClose={() => {
            setShowCrearMovimientoBodega(false);
            setMovimientoEditando(null);
          }}
          onCreated={() => {
            cargarMovimientosBodega();
            setShowCrearMovimientoBodega(false);
            setMovimientoEditando(null);
          }}
        />
      )}

      {/* Modal Crear/Editar Rendición */}
      {showCrearRendicion && (
        <div className="modal-overlay" onClick={() => { setShowCrearRendicion(false); resetFormRendicion(); }}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{rendicionEditando ? 'Editar' : 'Nueva'} Rendición</h2>
              <button className="close-btn" onClick={() => { setShowCrearRendicion(false); resetFormRendicion(); }}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitRendicion}>
              {/* Información del Proyecto */}
              <div className="info-box">
                <p><strong>Proyecto:</strong> {proyecto.nombre}</p>
                <p><strong>Cliente:</strong> {proyecto.cliente_nombre}</p>
              </div>

              {/* Datos de la Rendición */}
              <div className="form-row">
                <div className="form-group">
                  <label>Tipo de Comprobante *</label>
                  <select
                    name="tipo_comprobante"
                    value={formDataRendicion.tipo_comprobante}
                    onChange={handleInputChangeRendicion}
                    required
                  >
                    <option value="factura">Factura</option>
                    <option value="boleta">Boleta</option>
                    <option value="recibo">Recibo</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Número de Comprobante *</label>
                  <input
                    type="text"
                    name="numero_comprobante"
                    value={formDataRendicion.numero_comprobante}
                    onChange={handleInputChangeRendicion}
                    required
                    placeholder="Ej: 12345"
                  />
                </div>

                <div className="form-group">
                  <label>Fecha Comprobante *</label>
                  <input
                    type="date"
                    name="fecha_comprobante"
                    value={formDataRendicion.fecha_comprobante}
                    onChange={handleInputChangeRendicion}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Proveedor</label>
                  <select
                    name="proveedor"
                    value={formDataRendicion.proveedor}
                    onChange={handleInputChangeRendicion}
                  >
                    <option value="">Seleccionar...</option>
                    {proveedores.map(p => (
                      <option key={p.id} value={p.id}>{p.nombre}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Orden de Compra Relacionada</label>
                  <select
                    name="orden_compra"
                    value={formDataRendicion.orden_compra}
                    onChange={handleInputChangeRendicion}
                  >
                    <option value="">Seleccionar...</option>
                    {ordenesCompra.map(oc => (
                      <option key={oc.id} value={oc.id}>OC {oc.numero_oc}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  name="descripcion"
                  value={formDataRendicion.descripcion}
                  onChange={handleInputChangeRendicion}
                  rows="2"
                  placeholder="Descripción general del gasto..."
                />
              </div>

              {/* Detalles de la Rendición */}
              <div className="detalles-rendicion-section">
                <div className="section-header">
                  <h3>Detalles del Comprobante</h3>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleAgregarDetalleRendicion}
                  >
                    <FiPlus /> Agregar Detalle
                  </button>
                </div>

                {detallesRendicion.length > 0 ? (
                  <div className="detalles-table-container">
                    <table className="detalles-table">
                      <thead>
                        <tr>
                          <th>Descripción *</th>
                          <th>Monto *</th>
                          <th>Categoría</th>
                          <th>Notas</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detallesRendicion.map(detalle => (
                          <tr key={detalle.id}>
                            <td>
                              <input
                                type="text"
                                value={detalle.descripcion}
                                onChange={(e) => handleChangeDetalleRendicion(detalle.id, 'descripcion', e.target.value)}
                                required
                                placeholder="Descripción del gasto"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                value={detalle.monto}
                                onChange={(e) => handleChangeDetalleRendicion(detalle.id, 'monto', e.target.value)}
                                required
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                              />
                            </td>
                            <td>
                              <select
                                value={detalle.categoria}
                                onChange={(e) => handleChangeDetalleRendicion(detalle.id, 'categoria', e.target.value)}
                              >
                                <option value="">Seleccionar...</option>
                                {categorias.map(cat => (
                                  <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                type="text"
                                value={detalle.notas}
                                onChange={(e) => handleChangeDetalleRendicion(detalle.id, 'notas', e.target.value)}
                                placeholder="Notas..."
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn-icon danger"
                                onClick={() => handleEliminarDetalleRendicion(detalle)}
                                title="Eliminar"
                              >
                                <FiTrash2 />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="1"><strong>Total:</strong></td>
                          <td><strong>{formatCurrency(detallesRendicion.reduce((sum, d) => sum + (parseFloat(d.monto) || 0), 0))}</strong></td>
                          <td colSpan="3"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state-small">
                    <p>No hay detalles. Haz clic en "Agregar Detalle" para comenzar.</p>
                  </div>
                )}
              </div>

              {/* Botones de acción */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setShowCrearRendicion(false); resetFormRendicion(); }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {rendicionEditando ? 'Actualizar' : 'Crear'} Rendición
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para seleccionar items y crear presupuesto de costos */}
      {presupuestoParaCostos && (
        <SeleccionarItemsPresupuestoModal
          isOpen={showSeleccionarItemsModal}
          onClose={() => {
            setShowSeleccionarItemsModal(false);
            setPresupuestoParaCostos(null);
          }}
          presupuestoVenta={presupuestoParaCostos}
          onItemsSelected={handleCrearPresupuestoCostos}
        />
      )}

      {/* Modal para asignar áreas */}
      {presupuestoParaAsignarAreas && (
        <AsignarAreasModal
          isOpen={asignarAreasModalOpen}
          presupuestoId={presupuestoParaAsignarAreas.id}
          tipo={tipoPresupuestoParaAsignarAreas}
          cuentaId={proyecto.cuenta}
          onClose={() => {
            setAsignarAreasModalOpen(false);
            setPresupuestoParaAsignarAreas(null);
          }}
          onSuccess={() => {
            // Recargar presupuestos
            if (tipoPresupuestoParaAsignarAreas === 'venta') {
              cargarPresupuestosVenta();
            } else {
              cargarPresupuestosCosto();
            }
          }}
        />
      )}

      {/* Modal para Asignar Áreas desde Catálogo */}
      <AsignarDesdeCatalogoModal
        isOpen={asignarDesdeCatalogoModalOpen}
        onClose={() => {
          setAsignarDesdeCatalogoModalOpen(false);
          setPresupuestoParaAsignarCatalogo(null);
        }}
        presupuestoVentaId={tipoPresupuestoParaAsignarCatalogo === 'venta' ? presupuestoParaAsignarCatalogo?.id : null}
        presupuestoCostoId={tipoPresupuestoParaAsignarCatalogo === 'costo' ? presupuestoParaAsignarCatalogo?.id : null}
        cuentaId={proyecto.cuenta}
        tipo={tipoPresupuestoParaAsignarCatalogo}
        onAssigned={() => {
          if (tipoPresupuestoParaAsignarCatalogo === 'venta') {
            cargarPresupuestosVenta();
          } else {
            cargarPresupuestosCosto();
          }
        }}
      />

      {/* Modal para Agregar Área al Presupuesto */}
      <AgregarAreaPresupuestoModal
        isOpen={agregarAreaModalOpen}
        onClose={() => {
          setAgregarAreaModalOpen(false);
          setPresupuestoParaAgregarArea(null);
        }}
        presupuestoVentaId={tipoPresupuestoParaAgregarArea === 'venta' ? presupuestoParaAgregarArea?.id : null}
        presupuestoCostoId={tipoPresupuestoParaAgregarArea === 'costo' ? presupuestoParaAgregarArea?.id : null}
        tipo={tipoPresupuestoParaAgregarArea}
        onSuccess={() => {
          if (tipoPresupuestoParaAgregarArea === 'venta') {
            cargarPresupuestosVenta();
          } else {
            cargarPresupuestosCosto();
          }
        }}
      />

      {/* Modal para Asignar Presupuesto de Venta a Costo */}
      {asignarPresupuestoCostoModalOpen && presupuestoVentaParaCosto && (
        <AsignarPresupuestoCostoModal
          isOpen={asignarPresupuestoCostoModalOpen}
          presupuestoVenta={presupuestoVentaParaCosto}
          onClose={() => {
            setAsignarPresupuestoCostoModalOpen(false);
            setPresupuestoVentaParaCosto(null);
          }}
          onSuccess={() => {
            cargarPresupuestosVenta();
            cargarPresupuestosCosto();
            setActiveTab('presupuesto-costo');
          }}
        />
      )}

      {/* Modal para Ver/Editar Orden de Compra */}
      {showVerEditarOC && ordenCompraParaVer && (
        <VerEditarOrdenCompraModal
          ordenCompra={ordenCompraParaVer}
          onClose={() => setShowVerEditarOC(false)}
          onUpdated={() => cargarOrdenesCompra()}
          readOnly={false}
        />
      )}

      {/* Modal Pantalla Completa - Detalle de Costos Gastados */}
      {showModalDetalleCostos && (
        <div
          className="modal-overlay-fullscreen"
          onClick={() => setShowModalDetalleCostos(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            padding: '2rem'
          }}
        >
          <div
            className="modal-fullscreen"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              width: '95%',
              height: '90%',
              maxWidth: '1600px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              overflow: 'hidden'
            }}
          >
            {/* Header del Modal */}
            <div
              style={{
                background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
                padding: '1.5rem 2rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px'
              }}
            >
              <h2 style={{
                color: 'white',
                fontSize: '1.5rem',
                fontWeight: '700',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                💸 Detalle de Costos Gastados en Presupuesto
              </h2>
              <button
                onClick={() => setShowModalDetalleCostos(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  fontSize: '1.5rem',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  backdropFilter: 'blur(10px)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <FiX />
              </button>
            </div>

            {/* Contenido del Modal */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: '2rem',
              backgroundColor: '#f9fafb'
            }}>
              {detalleCostosGastados && detalleCostosGastados.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    backgroundColor: 'white',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <thead>
                      <tr style={{
                        background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
                        color: 'white'
                      }}>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '0.95rem' }}>Documento</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '0.95rem' }}>Área</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '0.95rem' }}>Partida</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '0.95rem' }}>Recurso</th>
                        <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, fontSize: '0.95rem' }}>Cantidad</th>
                        <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, fontSize: '0.95rem' }}>Precio Unit.</th>
                        <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, fontSize: '0.95rem' }}>Total</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '0.95rem' }}>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalleCostosGastados.map((detalle, index) => (
                        <tr key={index} style={{
                          borderBottom: '1px solid #e5e7eb',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <td style={{ padding: '14px', fontSize: '0.9rem' }}>
                            <div style={{ fontWeight: 600, color: '#1f2937' }}>{detalle.tipo_documento}</div>
                            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '2px' }}>
                              {detalle.numero_documento}
                            </div>
                          </td>
                          <td style={{ padding: '14px', fontSize: '0.9rem', color: '#374151' }}>{detalle.area}</td>
                          <td style={{ padding: '14px', fontSize: '0.9rem', color: '#374151' }}>{detalle.partida}</td>
                          <td style={{ padding: '14px', fontSize: '0.9rem' }}>
                            <div style={{ fontWeight: 600, color: '#1f2937' }}>{detalle.recurso}</div>
                            {detalle.descripcion && (
                              <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '2px' }}>
                                {detalle.descripcion}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '14px', textAlign: 'right', fontSize: '0.9rem', color: '#374151' }}>
                            {parseFloat(detalle.cantidad).toFixed(2)}
                          </td>
                          <td style={{ padding: '14px', textAlign: 'right', fontSize: '0.9rem', color: '#374151' }}>
                            {(() => {
                              const formatCurrency = (value) => {
                                return new Intl.NumberFormat('es-CL', {
                                  style: 'currency',
                                  currency: 'CLP',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                }).format(value || 0);
                              };
                              return formatCurrency(detalle.precio_unitario);
                            })()}
                          </td>
                          <td style={{
                            padding: '14px',
                            textAlign: 'right',
                            fontSize: '0.95rem',
                            fontWeight: 700,
                            color: '#ef4444'
                          }}>
                            {(() => {
                              const formatCurrency = (value) => {
                                return new Intl.NumberFormat('es-CL', {
                                  style: 'currency',
                                  currency: 'CLP',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                }).format(value || 0);
                              };
                              return formatCurrency(detalle.total);
                            })()}
                          </td>
                          <td style={{ padding: '14px', fontSize: '0.85rem', color: '#6b7280' }}>
                            {new Date(detalle.fecha_imputacion).toLocaleDateString('es-CL')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{
                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                        fontWeight: 700,
                        borderTop: '3px solid #69B32D'
                      }}>
                        <td colSpan="6" style={{
                          padding: '16px',
                          textAlign: 'right',
                          fontSize: '1.1rem',
                          color: '#1f2937'
                        }}>
                          TOTAL GASTADO:
                        </td>
                        <td style={{
                          padding: '16px',
                          textAlign: 'right',
                          color: '#ef4444',
                          fontSize: '1.2rem',
                          fontWeight: 700
                        }}>
                          {(() => {
                            const formatCurrency = (value) => {
                              return new Intl.NumberFormat('es-CL', {
                                style: 'currency',
                                currency: 'CLP',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(value || 0);
                            };
                            return formatCurrency(detalleCostosGastados.reduce((sum, d) => sum + parseFloat(d.total || 0), 0));
                          })()}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem',
                  color: '#6b7280',
                  fontSize: '1.1rem'
                }}>
                  No hay costos gastados registrados
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DetalleProyectoModal;
